<template>
    <div class="container-fluid calendar-editor-v2" :class="calendarModeClass">
        <div class="calendar-workspace-toolbar">
            <div v-if="selection.docente_id" class="alert alert-info" style="margin-bottom:10px">
                <a :href="teacherScheduleUrl(selection.docente_id, range.from, range.to)" target="_blank" rel="noopener">Consulta gli impegni del docente nel periodo visualizzato</a>
            </div>
            <div v-if="scheduleConflictLinks.length" class="alert alert-warning" role="status">
                <strong>{{ scheduleConflictMessage || 'Verifica gli impegni delle risorse selezionate.' }}</strong>
                <div v-for="link in scheduleConflictLinks" :key="link.url"><a :href="link.url" target="_blank" rel="noopener">{{ link.label }}</a></div>
                <button type="button" class="btn btn-xs btn-default" @click="scheduleConflictLinks = []">Chiudi</button>
            </div>
            <div class="workspace-heading">
                <strong>Calendari visualizzati</strong>
                <span class="workspace-mode-badge" :class="'workspace-mode-' + (workspace.mode || 'single')">
                    {{ workspaceModeLabel }}
                </span>
                <span class="workspace-course-count">{{ selectedCourseIds.length }}/{{ workspaceCourseLimit }}</span>
                <button type="button" class="btn btn-xs btn-default workspace-guide-button" @click="openMultiCourseGuide">
                    <i class="voyager-info-circled"></i> Guida calendario
                </button>
                <button type="button" class="btn btn-xs btn-info workspace-guide-button" @click="orarioWebWizardVisible = true">
                    <i class="voyager-upload"></i> Importa OrarioWeb
                </button>
            </div>
            <div class="workspace-selected-courses" aria-label="Calendari selezionati">
                <span
                    v-for="choice in selectedWorkspaceChoices"
                    :key="choice.key"
                    class="workspace-selected-chip"
                    :class="{ 'workspace-selected-chip-articulated': choice.is_articulated }"
                >
                    <span class="workspace-course-color" :style="workspaceChoiceColor(choice)"></span>
                    <span class="workspace-chip-label">{{ choice.display_label }}</span>
                    <small v-if="choice.is_articulated">{{ choice.family_ids.length }} componenti</small>
                    <i v-if="isMandatoryWorkspaceChoice(choice)" class="voyager-lock" title="Corso di partenza"></i>
                    <button v-else type="button" aria-label="Rimuovi il corso dalla vista" :disabled="workspaceCourseLoading" @click="toggleWorkspaceCourse(choice, false)">&times;</button>
                </span>

                <button v-if="usesWorkspaceCourseDialog" type="button" class="btn btn-sm btn-primary workspace-add-course" @click="openWorkspaceCourseDialog">
                    <i class="voyager-plus"></i> Aggiungi corso
                </button>
            </div>

            <div v-if="!usesWorkspaceCourseDialog && unselectedWorkspaceChoices.length" class="workspace-course-suggestions">
                <span>Aggiungi alla vista:</span>
                <button
                    v-for="choice in unselectedWorkspaceChoices"
                    :key="choice.key"
                    type="button"
                    class="workspace-course-option"
                    :disabled="workspaceCourseLoading || !canAddWorkspaceChoice(choice)"
                    @click="toggleWorkspaceCourse(choice, true)"
                >
                    <i class="voyager-plus"></i> {{ choice.display_label }}
                    <small v-if="choice.is_articulated">articolato · {{ choice.family_ids.length }} componenti</small>
                </button>
            </div>
            <div v-if="workspaceCourseMessage" class="workspace-course-message" role="status">{{ workspaceCourseMessage }}</div>
            <p class="workspace-help">
                Puoi visualizzare fino a {{ workspaceCourseLimit }} calendari. Nelle articolazioni il corso comune non può sovrapporsi ai profili, mentre i profili distinti possono procedere in parallelo; anche i corsi indipendenti restano pianificazioni parallele.
            </p>
        </div>

        <div v-if="workspaceCourseDialogVisible" class="calendar-modal-backdrop" @click.self="closeWorkspaceCourseDialog">
            <section ref="workspaceCourseDialog" class="calendar-modal calendar-workspace-course-dialog" role="dialog" aria-modal="true" aria-labelledby="calendar-workspace-course-dialog-title" tabindex="-1" @keydown.esc.stop="closeWorkspaceCourseDialog">
                <div class="calendar-modal-header workspace-course-dialog-header">
                    <div>
                        <h4 id="calendar-workspace-course-dialog-title">Aggiungi corsi alla vista</h4>
                        <p>Cerca tra i corsi che puoi gestire nella stessa sede. Le articolazioni vengono aggiunte con tutte le loro componenti.</p>
                    </div>
                    <button type="button" class="multicourse-guide-close" aria-label="Chiudi la selezione corsi" @click="closeWorkspaceCourseDialog">&times;</button>
                </div>
                <div class="calendar-modal-body workspace-course-dialog-body">
                    <div class="workspace-course-dialog-tools">
                        <label for="workspace-course-search">Cerca corso</label>
                        <input id="workspace-course-search" ref="workspaceCourseSearch" v-model.trim="workspaceCourseSearch" type="search" class="form-control" placeholder="Codice o titolo del corso">
                        <span><strong>{{ selectedCourseIds.length }}</strong> di {{ workspaceCourseLimit }} calendari selezionati</span>
                    </div>

                    <div v-if="workspaceCourseMessage" class="alert alert-info">{{ workspaceCourseMessage }}</div>
                    <div v-if="!filteredWorkspaceChoices.length" class="workspace-course-dialog-empty">Nessun corso corrisponde alla ricerca.</div>
                    <div v-else class="workspace-course-dialog-list">
                        <button
                            v-for="choice in filteredWorkspaceChoices"
                            :key="choice.key"
                            type="button"
                            class="workspace-course-dialog-option"
                            :class="{ 'is-selected': isWorkspaceChoiceSelected(choice), 'is-articulated': choice.is_articulated }"
                            :disabled="workspaceCourseLoading || (!isWorkspaceChoiceSelected(choice) && !canAddWorkspaceChoice(choice))"
                            @click="toggleWorkspaceCourse(choice, !isWorkspaceChoiceSelected(choice))"
                        >
                            <i :class="isWorkspaceChoiceSelected(choice) ? 'voyager-check' : 'voyager-plus'"></i>
                            <span>
                                <strong>{{ choice.display_label }}</strong>
                                <small v-if="choice.is_articulated">Articolazione completa · {{ choice.family_ids.length }} componenti</small>
                            </span>
                            <em v-if="!isWorkspaceChoiceSelected(choice) && !canAddWorkspaceChoice(choice)">Limite raggiunto</em>
                            <em v-else-if="isWorkspaceChoiceSelected(choice)">Nella vista</em>
                        </button>
                    </div>
                </div>
                <div class="calendar-modal-actions">
                    <button type="button" class="btn btn-primary" @click="closeWorkspaceCourseDialog">Fine</button>
                </div>
            </section>
        </div>

        <orario-web-import-wizard
            :visible="orarioWebWizardVisible"
            :config="config"
            :course="corso"
            @close="orarioWebWizardVisible = false"
            @imported="onOrarioWebImported"
        />

        <div v-if="provisionalLocks.length" class="alert alert-warning calendar-provisional-locks">
            <strong>Calendario applicato provvisoriamente.</strong>
            Gli intervalli evidenziati restano consultabili ma non modificabili fino all'approvazione o al rollback dal Generatore di calendari.
        </div>

        <div v-if="ddrLocks.length" class="alert alert-danger calendar-ddr-locks">
            <strong>Giorni protetti da DDR.</strong>
            Le date coperte da una DDR restano visibili in sola lettura e vengono escluse dalle modifiche alla fascia di lavoro.
        </div>

        <div v-if="multiCourseGuideVisible" class="calendar-modal-backdrop" @click.self="closeMultiCourseGuide">
            <section ref="multiCourseGuide" class="calendar-modal calendar-multicourse-guide" role="dialog" aria-modal="true" aria-labelledby="calendar-multicourse-guide-title" tabindex="-1" @keydown.esc.stop="closeMultiCourseGuide">
                <div class="calendar-modal-header multicourse-guide-header">
                    <div>
                        <span class="multicourse-guide-kicker">Calendar V2</span>
                        <h4 id="calendar-multicourse-guide-title">Come usare il calendario visivo</h4>
                        <p>Dalla consultazione alla modifica: scelta del corso, risorse, disponibilità, controlli e gestione multi-corso.</p>
                    </div>
                    <button type="button" class="multicourse-guide-close" aria-label="Chiudi la guida multi-corso" @click="closeMultiCourseGuide">&times;</button>
                </div>

                <div class="calendar-modal-body multicourse-guide-body">
                    <section class="multicourse-guide-steps calendar-guide-first-steps">
                        <h5>Flusso di lavoro</h5>
                        <ol>
                            <li><strong>Controlla la vista:</strong> scegli periodo, giorni e aule e verifica quali corsi sono caricati nella riga superiore. In Consultazione puoi leggere il calendario senza modificarlo.</li>
                            <li><strong>Passa a Modifica:</strong> seleziona il modulo del corso corretto, quindi docente oppure tutor ed eventuali co-docente e aula. Il corso indicato accanto al modulo è quello sul quale verrà salvata la lezione.</li>
                            <li><strong>Inserisci o rimuovi una lezione:</strong> clicca una cella libera per aggiungerla; clicca una lezione esistente per avviare la rimozione. Le azioni massive e le ricorrenze mostrano un'anteprima prima della conferma.</li>
                            <li><strong>Leggi gli avvisi:</strong> una cella occupata da una risorsa non è utilizzabile. Se l'orario è fuori dalla disponibilità dichiarata del docente, Calendar V2 lo segnala e chiede una conferma esplicita prima del salvataggio.</li>
                            <li><strong>Controlla il risultato:</strong> osserva ore pianificate e residue, poi usa “Controlla calendario” per individuare sovrapposizioni e incoerenze prima di terminare.</li>
                        </ol>
                    </section>

                    <div class="multicourse-guide-notes calendar-guide-availability-note">
                        <p><strong>Disponibilità dei docenti:</strong> si gestiscono nella scheda della risorsa umana, indicando periodo, giorni, orari, ente e sede. Il Calendar V2 le usa come controllo durante l'inserimento; il Generatore di calendari le usa per costruire la simulazione.</p>
                        <p><strong>Nessuna disponibilità registrata:</strong> la risorsa viene considerata senza limitazioni dichiarate. Per ottenere controlli e simulazioni attendibili, registra prima le fasce effettivamente comunicate dal docente.</p>
                    </div>

                    <h5 class="calendar-guide-section-title">Lavorare con più corsi</h5>
                    <div class="multicourse-guide-modes">
                        <article>
                            <span class="workspace-mode-badge workspace-mode-single">Corso singolo</span>
                            <h5>Un solo calendario</h5>
                            <p>È selezionato soltanto il corso aperto. Moduli, docenti, ore e statistiche appartengono a quel corso.</p>
                        </article>
                        <article>
                            <span class="workspace-mode-badge workspace-mode-articulated">Calendario articolato</span>
                            <h5>Comune e profili coordinati</h5>
                            <p>Se un corso appartiene a un'articolazione, tutte le componenti vengono caricate insieme. Il corso comune è esclusivo rispetto a ogni profilo; Profilo A e Profilo B possono invece svolgersi nello stesso orario, se docenti e tutor non sono in conflitto. Il corso di partenza e la sua famiglia non sono deselezionabili.</p>
                        </article>
                        <article>
                            <span class="workspace-mode-badge workspace-mode-parallel">Pianificazioni parallele</span>
                            <h5>Corsi indipendenti</h5>
                            <p>Puoi aggiungere corsi non articolati alla stessa vista. Le lezioni possono svolgersi contemporaneamente, ma restano bloccati i conflitti di docenti, tutor e co-docenti.</p>
                        </article>
                        <article>
                            <span class="workspace-mode-badge workspace-mode-mixed">Modalità mista</span>
                            <h5>Articolazioni e corsi paralleli</h5>
                            <p>Una o più famiglie articolate possono essere confrontate con corsi indipendenti: in ogni famiglia il comune blocca entrambi i profili, mentre i profili distinti possono procedere in parallelo. Anche i gruppi indipendenti restano paralleli.</p>
                        </article>
                    </div>

                    <section class="multicourse-guide-steps">
                        <h5>Procedura multi-corso</h5>
                        <ol>
                            <li><strong>Scegli i corsi</strong> dalle proposte in alto oppure con <strong>Aggiungi corso</strong>. Se selezioni una componente articolata, Calendar V2 include automaticamente l'intera famiglia.</li>
                            <li><strong>Controlla il badge modalità</strong>: indica se stai lavorando su corso singolo, calendario articolato, corsi paralleli o una combinazione mista.</li>
                            <li><strong>Imposta periodo, giorni e aule</strong>. I filtri definiscono la porzione di griglia visualizzata per tutto il workspace.</li>
                            <li><strong>Passa a Modifica</strong> e scegli il modulo: il nome del corso riportato nel selettore identifica il calendario sul quale verrà salvata la lezione.</li>
                            <li><strong>Seleziona docente, tutor, co-docente e aula</strong>, quindi clicca una cella libera. Ogni lezione rimane sempre associata al proprio corso reale.</li>
                            <li><strong>Usa Controlla calendario</strong> prima di terminare, soprattutto dopo modifiche su corsi con risorse condivise.</li>
                        </ol>
                    </section>

                    <div class="multicourse-guide-notes">
                        <p><strong>Aule non bloccanti:</strong> l'aula resta selezionabile, visibile, filtrabile e salvata come informazione della lezione, ma il sistema non impedisce una sovrapposizione della stessa aula perché l'anagrafica potrebbe non essere completa o aggiornata.</p>
                        <p><strong>Riepilogo e residui:</strong> la testata mostra valori aggregati sul workspace; selezionando un modulo, i residui visualizzati si riferiscono al modulo e al docente attivi.</p>
                        <p><strong>Copia, taglia e ricorrenze:</strong> le azioni di giornata operano sul corso attivo, determinato dal modulo selezionato. L'anteprima segnala gli eventuali conflitti prima della conferma.</p>
                        <p><strong>Calendari in verifica:</strong> gli intervalli applicati provvisoriamente dal Generatore restano visibili ma non modificabili fino a consolidamento o rollback.</p>
                        <p><strong>Limite della vista:</strong> puoi caricare fino a 10 calendari contemporaneamente. Le componenti di un'articolazione rientrano tutte nel conteggio.</p>
                    </div>
                </div>

                <div class="calendar-modal-actions">
                    <button type="button" class="btn btn-primary" @click="closeMultiCourseGuide">Ho capito, chiudi la guida</button>
                </div>
            </section>
        </div>

        <div class="calendar-course-summary">
            <div class="summary-course-title">
                <strong>{{ workspaceTitle }}</strong>
                <span v-if="workspace.courses && workspace.courses.length > 1">({{ workspace.courses.length }} corsi)</span>
            </div>
            <div class="summary-metric summary-neutral">
                <span>Inizio</span>
                <strong>{{ formatDate(workspaceStartDate) }}</strong>
            </div>
            <div class="summary-metric summary-neutral">
                <span>Fine</span>
                <strong>{{ formatDate(workspaceEndDate) }}</strong>
            </div>
            <div class="summary-metric">
                <span>Ore moduli</span>
                <strong>{{ courseTotalHours }}h</strong>
            </div>
            <div class="summary-metric summary-success">
                <span>Pianificate</span>
                <strong>{{ coursePlannedHours }}h</strong>
            </div>
            <div class="summary-metric" :class="courseRemainingHours > 0 ? 'summary-warning' : 'summary-success'">
                <span>Da pianificare</span>
                <strong>{{ courseRemainingHours }}h</strong>
            </div>
            <div class="summary-progress">
                <div class="summary-progress-track">
                    <div class="summary-progress-bar" :style="{ width: courseProgressPercent + '%' }"></div>
                </div>
                <span>{{ courseProgressPercent }}%</span>
            </div>
            <div class="summary-hours">
                <span>Orario corso attivo: <strong>{{ activeCourse.ora_inizio || '--:--' }} - {{ activeCourse.ora_fine || '--:--' }}</strong></span>
                <button type="button" class="btn btn-xs btn-default" @click="openCourseHoursModal">
                    Modifica fascia di lavoro
                </button>
            </div>
        </div>

        <div v-if="provisionalLocks.length" class="alert alert-warning workspace-lock-alert">
            <strong>Calendario in verifica.</strong>
            Gli intervalli contrassegnati appartengono a un'applicazione provvisoria e sono consultabili ma non modificabili fino al consolidamento o al rollback.
        </div>

        <div v-if="workBand" class="alert alert-info calendar-work-band-alert">
            <strong>Fascia di lavoro temporanea:</strong>
            {{ formatDate(workBand.date_from) }} – {{ formatDate(workBand.date_to) }},
            {{ workBand.ora_inizio }} – {{ workBand.ora_fine }}.
            <span v-if="workBandDdrDayCount">{{ workBandDdrDayCount }} {{ workBandDdrDayCount === 1 ? 'giorno DDR escluso' : 'giorni DDR esclusi' }}.</span>
            La fascia standard del corso non è stata modificata.
            <button type="button" class="btn btn-xs btn-link" @click="resetWorkBand">Ripristina vista standard</button>
        </div>

        <div v-if="hasMixedMinutePhases" class="alert alert-info calendar-phase-alert">
            <strong>Vista combinata con fasi :00 e :30.</strong>
            Ogni colonna rappresenta una lezione di 60 minuti. Per il corso attivo puoi aggiungere lezioni soltanto sulle partenze
            <strong>:{{ activeMinutePhase }}</strong>; le pianificazioni degli altri corsi restano visibili e modificabili.
        </div>

        <calendar-filters
            :range="range"
            :weekdays="weekdays"
            :mode="mode"
            :aule="meta.aule"
            :diagnostics-loading="diagnosticsLoading"
            @update="onFiltersUpdate"
            @diagnostics="runDiagnostics"
        />

        <calendar-stats
            v-if="showStats"
            :days="days"
            :slots="calendarSlots"
            :meta="meta"
            :diagnostics="diagnostics"
            :diagnostics-loading="diagnosticsLoading"
            @diagnostics="runDiagnostics"
            @close="showStats = false"
        />

        <calendar-picker
            v-model="selection"
            :stats="selectionStats"
            :moduli="meta.moduli"
            :docenti="meta.docenti"
            :tutors="activeTutors"
            :aule="activeRooms"
            :config="config"
        />

        <div v-if="mode === 'modifica'" class="calendar-edit-context-bar">
            <div class="context-main">
                <span class="context-pill context-mode">Modalità modifica</span>
                <span class="context-item">
                    <strong>Modulo:</strong> {{ selectedModuloLabel }}
                </span>
                <span class="context-item">
                    <strong>Docente:</strong> {{ selectedDocenteLabel }}
                </span>
                <span class="context-item" v-if="selectedTutorLabel">
                    <strong>Tutor:</strong> {{ selectedTutorLabel }}
                </span>
                <span class="context-item" v-if="selectedCodocenteLabel">
                    <strong>Co-docente:</strong> {{ selectedCodocenteLabel }}
                </span>
            </div>

            <div class="context-kpis">
                <span class="context-kpi" :class="getResidueClass(moduloResidue)">
                    Modulo: <strong>{{ moduloResidue }}h</strong>
                </span>
                <span class="context-kpi" :class="getResidueClass(docenteResidue)">
                    Docente: <strong>{{ docenteResidue }}h</strong>
                </span>
            </div>
        </div>

        <calendar-grid
            :days="days"
            :hours="displayHours"
            :slots="calendarSlots"
            :availability="activeAvailability"
            :mode="mode"
            :selection="selection"
            :active-course-id="activeCourseId"
            :active-minute-phase="activeMinutePhase"
            :mixed-minute-phases="hasMixedMinutePhases"
            :locked-ranges="workspace.locks || []"
            @cell-click="handleCellClick"
            @teacher-schedule="openTeacherSchedule"
            @day-click="openDayActions"
            @cell-context="openCellStaffModal"
            @availability-request="requestAvailabilityConfirmation"
        />

        <div v-if="dayActionModal.visible" class="calendar-modal-backdrop" @click.self="closeDayActions">
            <div class="calendar-modal calendar-modal-sm" role="dialog" aria-modal="true">
                <div class="calendar-modal-header">
                    <h4>{{ dayActionTitle }}</h4>
                </div>

                <div class="calendar-modal-body">
                    <p class="text-muted">
                        Ore visibili nella griglia: <strong>{{ dayActionSlots.length }}</strong>
                    </p>
                    <p v-if="dayClipboard" class="calendar-clipboard-info">
                        In clipboard: <strong>{{ clipboardLabel }}</strong>
                    </p>

                    <div class="day-action-list">
                        <button
                            type="button"
                            class="btn btn-default btn-block"
                            :disabled="!dayActionSlots.length"
                            @click="copyDay('copy')"
                        >
                            Copia giornata
                        </button>
                        <button
                            type="button"
                            class="btn btn-default btn-block"
                            :disabled="!dayActionSlots.length"
                            @click="copyDay('cut')"
                        >
                            Taglia giornata
                        </button>
                        <button
                            type="button"
                            class="btn btn-primary btn-block"
                            :disabled="!dayClipboard || pasteModal.loading"
                            @click="previewPasteDay"
                        >
                            Incolla qui con anteprima
                        </button>
                    </div>
                </div>

                <div class="calendar-modal-actions">
                    <button type="button" class="btn btn-default" @click="closeDayActions">
                        Chiudi
                    </button>
                </div>
            </div>
        </div>

        <div v-if="pasteModal.visible" class="calendar-modal-backdrop" @click.self="cancelPasteDay">
            <div class="calendar-modal calendar-modal-lg" role="dialog" aria-modal="true">
                <div class="calendar-modal-header">
                    <h4>{{ pasteModalTitle }}</h4>
                </div>

                <div class="calendar-modal-body">
                    <div v-if="pasteModal.loading" class="calendar-modal-loading">
                        Controllo sovrapposizioni in corso...
                    </div>

                    <template v-else-if="pasteModal.plan">
                        <div
                            v-for="warning in pasteModal.plan.warnings"
                            :key="warning"
                            class="calendar-preview-alert calendar-preview-warning"
                        >
                            {{ warning }}
                        </div>

                        <div
                            v-for="error in pasteModal.plan.errors"
                            :key="error"
                            class="calendar-preview-alert calendar-preview-error"
                        >
                            {{ error }}
                        </div>

                        <div class="calendar-preview-table">
                            <div
                                v-for="item in pasteModal.plan.items"
                                :key="item.source_id"
                                class="calendar-preview-row"
                                :class="{ 'calendar-preview-row-error': item.errors && item.errors.length }"
                            >
                                <div class="calendar-preview-main">
                                    <strong>{{ item.start }} - {{ item.end }}</strong>
                                    <span>{{ item.modulo_nome }}</span>
                                    <span>{{ item.docente_nome }}</span>
                                    <small v-if="item.tutor_nome">Tutor: {{ item.tutor_nome }}</small>
                                    <small v-if="item.codocente_nome">Co-docente: {{ item.codocente_nome }}</small>
                                </div>
                                <div v-if="item.errors && item.errors.length" class="calendar-preview-errors">
                                    <div v-for="error in item.errors" :key="error">
                                        {{ error }}
                                    </div>
                                </div>
                                <div v-else class="calendar-preview-ok">
                                    OK
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <div class="calendar-modal-actions">
                    <button
                        type="button"
                        class="btn btn-default"
                        :disabled="pasteModal.applying"
                        @click="cancelPasteDay"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="!pasteModal.plan || !pasteModal.plan.valid || pasteModal.loading || pasteModal.applying"
                        @click="applyPasteDay"
                    >
                        {{ pasteModal.applying ? 'Applicazione...' : 'Conferma incolla' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="staffModal.visible" class="calendar-modal-backdrop" @click.self="closeStaffModal">
            <div class="calendar-modal calendar-modal-md" role="dialog" aria-modal="true">
                <div class="calendar-modal-header">
                    <h4>Azioni cella</h4>
                </div>

                <div class="calendar-modal-body">
                    <p class="calendar-slot-summary">
                        <strong>{{ staffModalLabel }}</strong>
                    </p>

                    <div class="form-group">
                        <label>Tutor</label>
                        <select class="form-control" v-model="staffModal.form.tutor_id">
                            <option :value="null">-- Nessun tutor --</option>
                            <option v-for="tutor in staffTutors" :key="tutor.id" :value="tutor.id">
                                {{ tutor.full_name || tutor.name || tutor.nome }}
                            </option>
                        </select>
                    </div>

                    <div class="form-group codocente-autocomplete">
                        <label>Co-docente</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="staffModal.codocenteSearch"
                            placeholder="Cerca co-docente..."
                            @input="onStaffCodocenteSearch"
                            @focus="staffModal.showCodocenteResults = true"
                        >

                        <div v-if="staffModal.codocenteLabel" class="selected-codocente mt-1">
                            <span>{{ staffModal.codocenteLabel }}</span>
                            <button type="button" class="btn btn-xs btn-link p-0 ml-1" @click="clearStaffCodocente">
                                rimuovi
                            </button>
                        </div>

                        <div
                            v-if="staffModal.showCodocenteResults && staffModal.codocenteOptions.length"
                            class="codocente-results"
                        >
                            <div
                                v-for="item in staffModal.codocenteOptions"
                                :key="item.id"
                                class="codocente-result-item"
                                @click="selectStaffCodocente(item)"
                            >
                                {{ item.full_name || item.text || item.nome }}
                            </div>
                        </div>

                        <small class="text-muted" v-if="staffModal.codocenteLoading">Ricerca in corso...</small>
                    </div>

                    <p class="text-muted mb-0">
                        La modifica verrà applicata solo all’ora selezionata. Se la pianificazione copre più ore,
                        verrà divisa automaticamente.
                    </p>

                    <div class="cell-advanced-actions">
                        <button type="button" class="btn btn-default btn-block" @click="openRecurringModalFromStaff">
                            Ripianifica ora...
                        </button>
                    </div>
                </div>

                <div class="calendar-modal-actions">
                    <button
                        type="button"
                        class="btn btn-default"
                        :disabled="staffModal.loading"
                        @click="closeStaffModal"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="staffModal.loading"
                        @click="saveStaffModal"
                    >
                        {{ staffModal.loading ? 'Salvataggio...' : 'Salva cella' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="recurringModal.visible" class="calendar-modal-backdrop" @click.self="closeRecurringModal">
            <div class="calendar-modal calendar-modal-lg" role="dialog" aria-modal="true">
                <div class="calendar-modal-header">
                    <h4>Ripianifica ora ricorrente</h4>
                </div>

                <div class="calendar-modal-body">
                    <p class="calendar-slot-summary">
                        <strong>{{ recurringModalLabel }}</strong>
                    </p>

                    <div class="recurring-form-grid">
                        <div class="form-group">
                            <label>Da</label>
                            <input type="date" class="form-control" v-model="recurringModal.form.from">
                        </div>
                        <div class="form-group">
                            <label>A</label>
                            <input type="date" class="form-control" v-model="recurringModal.form.to">
                        </div>
                        <div class="form-group">
                            <label>Limite</label>
                            <select class="form-control" v-model="recurringModal.form.stop_mode">
                                <option value="module_remaining">Fino a completamento modulo/docente</option>
                                <option value="date">Fino alla data indicata</option>
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Occorrenze da proporre</label>
                            <input type="number" min="1" max="100" class="form-control" v-model.number="recurringModal.form.max_occurrences">
                            <small v-if="recurringModal.remaining_hours" class="text-muted">
                                Residue {{ formatHours(recurringModal.remaining_hours) }}h · slot da {{ formatHours(recurringModal.slot_duration || 1) }}h
                            </small>
                        </div>
                    </div>

                    <div class="form-group">
                        <label>Giorni</label>
                        <div class="recurring-weekdays">
                            <label v-for="day in recurringWeekdayOptions" :key="day.value">
                                <input type="checkbox" :value="day.value" v-model="recurringModal.form.weekdays">
                                {{ day.label }}
                            </label>
                        </div>
                    </div>

                    <label class="recurring-checkbox">
                        <input type="checkbox" v-model="recurringModal.form.skip_conflicts">
                        Salta automaticamente le date con conflitti
                    </label>

                    <div class="recurring-preview-actions">
                        <button
                            type="button"
                            class="btn btn-default"
                            :disabled="recurringModal.loading || recurringModal.applying"
                            @click="previewRecurring"
                        >
                            {{ recurringModal.loading ? 'Anteprima...' : 'Genera anteprima' }}
                        </button>
                        <span v-if="recurringModal.plan" class="text-muted">
                            Valide: {{ recurringModal.plan.valid_count }} · Conflitti: {{ recurringModal.plan.error_count }} · Ore: {{ recurringModal.plan.hours_to_plan }}h
                        </span>
                    </div>

                    <div v-if="recurringModal.plan" class="calendar-preview-table recurring-preview-table">
                        <div
                            v-for="item in recurringModal.plan.items"
                            :key="item.date + '_' + item.start"
                            class="calendar-preview-row"
                            :class="{ 'calendar-preview-row-error': item.errors && item.errors.length }"
                        >
                            <div class="calendar-preview-main">
                                <strong>{{ formatDate(item.date) }} · {{ item.start }} - {{ item.end }}</strong>
                                <span>{{ item.modulo_nome }}</span>
                                <span>{{ item.docente_nome }}</span>
                                <small v-if="item.tutor_nome">Tutor: {{ item.tutor_nome }}</small>
                                <small v-if="item.codocente_nome">Co-docente: {{ item.codocente_nome }}</small>
                            </div>
                            <div v-if="item.errors && item.errors.length" class="calendar-preview-errors">
                                <div v-for="error in item.errors" :key="error">
                                    {{ error }}
                                </div>
                            </div>
                            <div v-else class="calendar-preview-ok">
                                OK
                            </div>
                        </div>
                    </div>
                </div>

                <div class="calendar-modal-actions">
                    <button
                        type="button"
                        class="btn btn-default"
                        :disabled="recurringModal.applying"
                        @click="closeRecurringModal"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="!recurringCanApply || recurringModal.loading || recurringModal.applying"
                        @click="applyRecurring"
                    >
                        {{ recurringModal.applying ? 'Pianificazione...' : 'Conferma ricorrenza' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="courseHoursModal.visible" class="calendar-modal-backdrop" @click.self="closeCourseHoursModal">
            <div class="calendar-modal calendar-modal-sm" role="dialog" aria-modal="true">
                <div class="calendar-modal-header">
                    <h4>Modifica fascia di lavoro</h4>
                </div>

                <div class="calendar-modal-body">
                    <p class="text-muted">
                        Estende temporaneamente la griglia nel periodo scelto. Non cambia l’orario standard del corso e non salva nulla finché non inserisci una lezione.
                    </p>

                    <div class="row">
                        <div class="col-sm-6 form-group">
                            <label>Dal giorno</label>
                            <input type="date" class="form-control" v-model="courseHoursModal.form.date_from" :max="courseHoursModal.form.date_to">
                        </div>
                        <div class="col-sm-6 form-group">
                            <label>Al giorno</label>
                            <input type="date" class="form-control" v-model="courseHoursModal.form.date_to" :min="courseHoursModal.form.date_from">
                        </div>
                    </div>

                    <div class="form-group">
                        <label>Ora ingresso</label>
                        <input type="time" class="form-control" step="1800" v-model="courseHoursModal.form.ora_inizio">
                    </div>

                    <div class="form-group">
                        <label>Ora uscita</label>
                        <input type="time" class="form-control" step="1800" v-model="courseHoursModal.form.ora_fine">
                    </div>

                    <p class="text-muted mb-0">
                        I giorni coperti da DDR resteranno visibili ma bloccati. Per ogni nuova lezione fuori dall’orario standard verrà richiesta una motivazione.
                    </p>
                </div>

                <div class="calendar-modal-actions">
                    <button
                        type="button"
                        class="btn btn-default"
                        :disabled="courseHoursModal.loading"
                        @click="closeCourseHoursModal"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        :disabled="courseHoursModal.loading"
                        @click="applyWorkBand"
                    >
                        {{ courseHoursModal.loading ? 'Applicazione...' : 'Applica alla vista' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="deleteModal.visible" class="calendar-modal-backdrop" @click.self="cancelDeleteSlot">
            <div class="calendar-modal" role="dialog" aria-modal="true">
                <div class="calendar-modal-header">
                    <h4>Rimuovere la pianificazione?</h4>
                </div>

                <div class="calendar-modal-body">
                    <p>
                        Vuoi rimuovere
                        <strong>{{ deleteModalLabel }}</strong>
                        dal calendario?
                    </p>
                    <p class="text-muted mb-0">
                        La giornata verrà aggiornata e le disponibilità verranno ricalcolate subito.
                    </p>
                </div>

                <div class="calendar-modal-actions">
                    <button
                        type="button"
                        class="btn btn-default"
                        :disabled="deleteModal.loading"
                        @click="cancelDeleteSlot"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        :disabled="deleteModal.loading"
                        @click="confirmDeleteSlot"
                    >
                        {{ deleteModal.loading ? 'Rimozione...' : 'Sì, rimuovi' }}
                    </button>
                </div>
            </div>
        </div>

        <div v-if="outsideHoursModal.visible" class="calendar-modal-backdrop" @click.self="closeOutsideHoursModal">
            <div class="calendar-modal calendar-modal-sm" role="dialog" aria-modal="true">
                <div class="calendar-modal-header">
                    <h4>Pianificazione fuori orario</h4>
                </div>
                <div class="calendar-modal-body">
                    <p>
                        Questo intervallo non rientra nell’orario giornaliero del corso.
                        L’eccezione sarà registrata con operatore, data e motivazione.
                    </p>
                    <div class="form-group">
                        <label>Motivazione dell’eccezione</label>
                        <textarea class="form-control" rows="3" v-model.trim="outsideHoursModal.reason"
                                  placeholder="Indica perché è necessario pianificare fuori orario"></textarea>
                    </div>
                </div>
                <div class="calendar-modal-actions">
                    <button type="button" class="btn btn-default" :disabled="outsideHoursModal.loading" @click="closeOutsideHoursModal">
                        Annulla
                    </button>
                    <button type="button" class="btn btn-warning"
                            :disabled="outsideHoursModal.loading || !outsideHoursModal.reason"
                            @click="confirmOutsideHoursException">
                        {{ outsideHoursModal.loading ? 'Salvataggio...' : 'Aggiungi eccezione' }}
                    </button>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import CalendarFilters from './CalendarFilters.vue'
import CalendarPicker from './CalendarPicker.vue'
import CalendarGrid from './CalendarGrid.vue'
import CalendarStats from './CalendarStats.vue'
import OrarioWebImportWizard from './OrarioWebImportWizard.vue'

import api from './calendarApi.js'

export default {
    props: {
        config: {
            type: Object,
            required: true
        }
    },

    components: {
        CalendarFilters,
        CalendarPicker,
        CalendarGrid,
        CalendarStats,
        OrarioWebImportWizard
    },

    data() {
        return {
            corso: {},
            days: [],
            hours: [],
            workBand: null,
            calendarSlots: [],
            availability: {},
            availabilityByCourse: {},
            selectedCourseIds: (this.config.initial_course_ids || [this.config.corso_id]).map((id) => Number(id)),
            workspace: {
                mode: 'single',
                groups: [],
                courses: []
            },

            meta: {
                moduli: [],
                docenti: [],
                tutors: [],
                aule: []
            },

            mode: 'consultazione',

            range: {
                from: this.config.initial_range ? this.config.initial_range.from : null,
                to: this.config.initial_range ? this.config.initial_range.to : null
            },

            weekdays: [1, 2, 3, 4, 5],
            selectedAule: [],

            selection: {
                modulo_id: null,
                docente_id: null,
                tutor_id: null,
                codocente_id: null,
                aula_id: null,
                durata_tutor: 0
            },

            selectionStats: {},
            diagnostics: null,
            diagnosticsLoading: false,
            showStats: false,
            loading: false,
            error: null,
            multiCourseGuideVisible: false,
            orarioWebWizardVisible: Boolean(this.config.open_orarioweb),
            workspaceCourseLimit: 10,
            workspaceCourseDialogVisible: false,
            workspaceCourseSearch: '',
            workspaceCourseLoading: false,
            workspaceCourseMessage: '',
            deleteModal: {
                visible: false,
                loading: false,
                cell: null
            },
            outsideHoursModal: {
                visible: false,
                loading: false,
                reason: '',
                payload: null,
                declaredWarning: null
            },
            dayClipboard: null,
            dayActionModal: {
                visible: false,
                day: null
            },
            pasteModal: {
                visible: false,
                loading: false,
                applying: false,
                plan: null,
                targetDay: null
            },
            scheduleConflictLinks: [],
            scheduleConflictMessage: '',
            staffModal: {
                visible: false,
                loading: false,
                cell: null,
                form: {
                    tutor_id: null,
                    codocente_id: null
                },
                codocenteSearch: '',
                codocenteLabel: '',
                codocenteOptions: [],
                codocenteLoading: false,
                codocenteTimer: null,
                showCodocenteResults: false
            },
            courseHoursModal: {
                visible: false,
                loading: false,
                form: {
                    date_from: '',
                    date_to: '',
                    ora_inizio: '',
                    ora_fine: ''
                }
            },
            recurringModal: {
                visible: false,
                loading: false,
                applying: false,
                cell: null,
                plan: null,
                remaining_hours: 0,
                slot_duration: 1,
                form: {
                    from: '',
                    to: '',
                    weekdays: [],
                    stop_mode: 'module_remaining',
                    max_occurrences: 50,
                    skip_conflicts: true
                }
            }
        }
    },

    watch: {
        selection: {
            deep: true,
            handler() {
                if (this.selection.tutor_id && !this.activeTutors.some((item) => Number(item.id) === Number(this.selection.tutor_id))) {
                    this.$set(this.selection, 'tutor_id', null)
                }
                if (this.selection.aula_id && !this.activeRooms.some((item) => Number(item.id) === Number(this.selection.aula_id))) {
                    this.$set(this.selection, 'aula_id', null)
                }
                this.updateSelectionStats()
            }
        }
    },

    computed: {
        calendarModeClass() {
            return this.mode === 'modifica'
                ? 'calendar-mode-modifica'
                : 'calendar-mode-consultazione'
        },
        selectedModuloLabel() {
            const modulo = this.findById(this.meta.moduli, this.selection.modulo_id)
            return modulo ? ((modulo.course_label ? modulo.course_label + ' · ' : '') + (modulo.nome || modulo.modulo || 'Modulo selezionato')) : 'Nessun modulo selezionato'
        },

        workspaceCatalog() {
            return this.config.workspace_courses || []
        },

        workspaceChoices() {
            const catalog = this.workspaceCatalog
            const byId = new Map(catalog.map((course) => [Number(course.id), course]))
            const seen = new Set()

            return catalog.reduce((choices, course) => {
                const familyIds = Array.from(new Set(
                    (course.family_ids && course.family_ids.length ? course.family_ids : [course.id]).map(Number)
                )).sort((left, right) => left - right)
                const key = familyIds.join('-')
                if (seen.has(key)) return choices
                seen.add(key)

                const members = familyIds.map((id) => byId.get(id)).filter(Boolean)
                const articulated = familyIds.length > 1
                const first = members[0] || course
                choices.push({
                    id: Number(first.id),
                    key: key,
                    label: first.label,
                    display_label: articulated
                        ? 'Articolazione · ' + (first.label || first.titolo || 'Corso')
                        : (first.label || first.titolo || 'Corso'),
                    search_label: members.map((member) => member.label || member.titolo || '').join(' · '),
                    is_articulated: articulated,
                    family_ids: familyIds,
                    members: members
                })

                return choices
            }, [])
        },

        optionalWorkspaceChoices() {
            return this.workspaceChoices.filter((choice) => !this.isMandatoryWorkspaceChoice(choice))
        },

        selectedWorkspaceChoices() {
            return this.workspaceChoices.filter((choice) => this.isWorkspaceChoiceSelected(choice))
        },

        unselectedWorkspaceChoices() {
            return this.optionalWorkspaceChoices.filter((choice) => !this.isWorkspaceChoiceSelected(choice))
        },

        usesWorkspaceCourseDialog() {
            return this.optionalWorkspaceChoices.length > 3
        },

        filteredWorkspaceChoices() {
            const needle = this.workspaceCourseSearch.toLocaleLowerCase('it-IT')
            return this.optionalWorkspaceChoices.filter((choice) => {
                if (!needle) return true
                return (choice.display_label + ' ' + choice.search_label).toLocaleLowerCase('it-IT').indexOf(needle) !== -1
            })
        },

        workspaceTitle() {
            const courses = this.workspace.courses || []
            if (courses.length === 1) return courses[0].label || courses[0].titolo || 'Corso'
            if (courses.length > 1) return this.workspace.mode === 'articulated' ? 'Calendario unico articolato' : 'Pianificazione multi-corso'
            return this.corso.titolo || 'Corso'
        },

        workspaceModeLabel() {
            const labels = {
                single: 'Corso singolo',
                articulated: 'Calendario unico articolato',
                parallel: 'Pianificazioni parallele',
                mixed: 'Articolazioni e corsi paralleli'
            }
            return labels[this.workspace.mode] || labels.single
        },

        activeModulo() {
            return this.findById(this.meta.moduli, this.selection.modulo_id)
        },

        activeCourseId() {
            return this.activeModulo && this.activeModulo.corso_id
                ? Number(this.activeModulo.corso_id)
                : Number(this.config.corso_id)
        },

        activeCourse() {
            return (this.workspace.courses || []).find((course) => Number(course.id) === Number(this.activeCourseId)) || this.corso
        },

        activeAvailability() {
            return this.availabilityByCourse[this.activeCourseId] || this.availabilityByCourse[String(this.activeCourseId)] || this.availability || {}
        },

        displayHours() {
            const hours = (this.hours || []).slice()
            if (this.workBand) {
                hours.push(...this.generateHours(this.workBand.ora_inizio, this.workBand.ora_fine))
            }

            return Array.from(new Set(hours.filter(Boolean))).sort()
        },

        activeMinutePhase() {
            const reference = this.activeCourse.minute_phase || this.activeCourse.ora_inizio || '08:00'
            const minute = String(reference).indexOf(':') === -1
                ? String(reference)
                : String(reference).slice(3, 5)

            return minute === '30' ? '30' : '00'
        },

        visibleMinutePhases() {
            return Array.from(new Set(this.displayHours.map((hour) => {
                return String(hour).slice(3, 5) === '30' ? '30' : '00'
            }))).sort()
        },

        hasMixedMinutePhases() {
            return this.visibleMinutePhases.length > 1
        },

        provisionalLocks() {
            return (this.workspace.locks || []).filter((lock) => lock.lock_type !== 'ddr')
        },

        ddrLocks() {
            return (this.workspace.locks || []).filter((lock) => lock.lock_type === 'ddr')
        },

        workBandDdrDayCount() {
            if (!this.workBand) return 0

            return (this.days || []).filter((day) => {
                if (day.date < this.workBand.date_from || day.date > this.workBand.date_to) return false

                return this.ddrLocks.some((lock) => {
                    return Number(lock.course_id) === Number(this.activeCourseId) &&
                        day.date >= lock.date_from && day.date <= lock.date_to
                })
            }).length
        },

        activeTutors() {
            return (this.meta.tutors || []).filter((item) => !item.course_ids || item.course_ids.map(Number).indexOf(Number(this.activeCourseId)) !== -1)
        },

        activeRooms() {
            return (this.meta.aule || []).filter((item) => !item.course_ids || item.course_ids.map(Number).indexOf(Number(this.activeCourseId)) !== -1)
        },

        staffTutors() {
            const slot = this.staffModal.cell && this.staffModal.cell.slot ? this.staffModal.cell.slot : null
            const courseId = slot && slot.corso_id ? Number(slot.corso_id) : Number(this.activeCourseId)
            return (this.meta.tutors || []).filter((item) => !item.course_ids || item.course_ids.map(Number).indexOf(courseId) !== -1)
        },

        workspaceStartDate() {
            return (this.workspace.courses || []).map((course) => course.data_avvio).filter(Boolean).sort()[0] || this.corso.data_avvio
        },

        workspaceEndDate() {
            const dates = (this.workspace.courses || []).map((course) => course.data_fine).filter(Boolean).sort()
            return dates.length ? dates[dates.length - 1] : this.corso.data_fine
        },

        selectedDocenteLabel() {
            const docente = this.findById(this.meta.docenti, this.selection.docente_id)
            return docente ? (docente.full_name || docente.nome || 'Docente selezionato') : 'Nessun docente selezionato'
        },

        selectedTutorLabel() {
            const tutor = this.findById(this.meta.tutors, this.selection.tutor_id)
            return tutor ? (tutor.full_name || tutor.name || tutor.nome || '') : ''
        },

        selectedCodocenteLabel() {
            const docente = this.findById(this.meta.docenti, this.selection.codocente_id)
            return docente ? (docente.full_name || docente.nome || '') : ''
        },

        moduloResidue() {
            if (this.selectionStats && this.selectionStats.modulo) {
                if (typeof this.selectionStats.modulo.residue !== 'undefined') {
                    return this.selectionStats.modulo.residue
                }

                if (typeof this.selectionStats.modulo.remaining_hours !== 'undefined') {
                    return this.selectionStats.modulo.remaining_hours
                }
            }

            if (this.selectionStats && typeof this.selectionStats.modulo_residue !== 'undefined') {
                return this.selectionStats.modulo_residue
            }

            return 0
        },

        docenteResidue() {
            if (this.selectionStats && this.selectionStats.docente) {
                if (typeof this.selectionStats.docente.residue !== 'undefined') {
                    return this.selectionStats.docente.residue
                }

                if (typeof this.selectionStats.docente.remaining_hours !== 'undefined') {
                    return this.selectionStats.docente.remaining_hours
                }
            }

            if (this.selectionStats && typeof this.selectionStats.docente_residue !== 'undefined') {
                return this.selectionStats.docente_residue
            }

            return 0
        },

        courseTotalHours() {
            return this.formatHours((this.meta.moduli || []).reduce((total, modulo) => {
                return total + Number(modulo.durata || 0)
            }, 0))
        },

        coursePlannedHours() {
            return this.formatHours((this.meta.moduli || []).reduce((total, modulo) => {
                return total + Number(modulo.ore_pianificate || 0)
            }, 0))
        },

        courseRemainingHours() {
            const remaining = Number(this.courseTotalHours) - Number(this.coursePlannedHours)
            return this.formatHours(Math.max(0, remaining))
        },

        courseProgressPercent() {
            const total = Number(this.courseTotalHours)
            if (!total) return 0

            return Math.min(100, Math.round((Number(this.coursePlannedHours) / total) * 100))
        },

        deleteModalLabel() {
            const cell = this.deleteModal.cell || {}
            const slot = cell.slot || cell
            const parts = []

            if (slot.date || cell.date) {
                parts.push(slot.date || cell.date)
            }

            const start = this.getDeleteCellStart(cell)
            const end = this.getDeleteCellEnd(cell, start)

            if (start && end) {
                parts.push(start + ' - ' + end)
            }

            if (slot.modulo_nome) {
                parts.push(slot.modulo_nome)
            }

            if (slot.docente_nome) {
                parts.push(slot.docente_nome)
            }

            return parts.join(' · ') || 'questa pianificazione'
        },

        dayActionSlots() {
            const day = this.dayActionModal.day || {}
            if (!day.date) return []

            return (this.calendarSlots || []).filter((slot) => {
                return String(slot.date || slot.day) === String(day.date) &&
                    Number(slot.corso_id) === Number(this.activeCourseId)
            })
        },

        dayActionTitle() {
            const day = this.dayActionModal.day || {}
            return day.label ? 'Azioni ' + day.label : 'Azioni giornata'
        },

        clipboardLabel() {
            if (!this.dayClipboard) return ''

            const action = this.dayClipboard.operation === 'cut' ? 'Taglia' : 'Copia'
            return action + ' ' + (this.dayClipboard.label || this.dayClipboard.from) + ' (' + this.dayClipboard.count + ' ore)'
        },

        pasteModalTitle() {
            if (!this.pasteModal.plan) {
                return 'Anteprima incolla giornata'
            }

            const op = this.pasteModal.plan.operation === 'cut' ? 'Taglia/incolla' : 'Copia/incolla'
            return op + ' ' + this.pasteModal.plan.from + ' → ' + this.pasteModal.plan.to
        },

        staffModalLabel() {
            const cell = this.staffModal.cell || {}
            const slot = cell.slot || {}
            const parts = []

            if (cell.date) {
                parts.push(cell.date)
            }

            if (cell.start || cell.hour) {
                parts.push((cell.start || cell.hour) + ' - ' + (cell.end || this.addOneHour(cell.start || cell.hour)))
            }

            if (slot.modulo_nome) {
                parts.push(slot.modulo_nome)
            }

            if (slot.docente_nome) {
                parts.push(slot.docente_nome)
            }

            return parts.join(' · ') || 'Cella selezionata'
        },

        recurringModalLabel() {
            const cell = this.recurringModal.cell || {}
            const slot = cell.slot || {}
            const parts = []

            if (cell.date) {
                parts.push(cell.date)
            }

            if (cell.start || cell.hour) {
                parts.push((cell.start || cell.hour) + ' - ' + (cell.end || this.addOneHour(cell.start || cell.hour)))
            }

            if (slot.modulo_nome) {
                parts.push(slot.modulo_nome)
            }

            if (slot.docente_nome) {
                parts.push(slot.docente_nome)
            }

            return parts.join(' · ') || 'Ora selezionata'
        },

        recurringWeekdayOptions() {
            return [
                { value: 1, label: 'Lun' },
                { value: 2, label: 'Mar' },
                { value: 3, label: 'Mer' },
                { value: 4, label: 'Gio' },
                { value: 5, label: 'Ven' },
                { value: 6, label: 'Sab' },
                { value: 7, label: 'Dom' }
            ]
        },

        recurringCanApply() {
            const plan = this.recurringModal.plan
            if (!plan || !plan.valid) return false

            if (!this.recurringModal.form.skip_conflicts && Number(plan.error_count || 0) > 0) {
                return false
            }

            return Number(plan.valid_count || 0) > 0
        }
    },

    methods: {
        openMultiCourseGuide() {
            this.multiCourseGuideVisible = true
            this.$nextTick(() => {
                if (this.$refs.multiCourseGuide) this.$refs.multiCourseGuide.focus()
            })
        },

        closeMultiCourseGuide() {
            this.multiCourseGuideVisible = false
        },

        async onOrarioWebImported() {
            await this.reloadCalendar()
            await this.updateSelectionStats()
            if (window.toastr) window.toastr.success('Calendario OrarioWeb importato correttamente')
        },

        openWorkspaceCourseDialog() {
            this.workspaceCourseDialogVisible = true
            this.workspaceCourseSearch = ''
            this.workspaceCourseMessage = ''
            this.$nextTick(() => {
                if (this.$refs.workspaceCourseDialog) this.$refs.workspaceCourseDialog.focus()
                if (this.$refs.workspaceCourseSearch) this.$refs.workspaceCourseSearch.focus()
            })
        },

        closeWorkspaceCourseDialog() {
            if (this.workspaceCourseLoading) return
            this.workspaceCourseDialogVisible = false
            this.workspaceCourseSearch = ''
            this.workspaceCourseMessage = ''
        },

        onCalendarWindowKeydown(event) {
            if (event.key === 'Escape' && this.multiCourseGuideVisible) this.closeMultiCourseGuide()
            if (event.key === 'Escape' && this.workspaceCourseDialogVisible) this.closeWorkspaceCourseDialog()
        },

        isWorkspaceCourseSelected(courseId) {
            return this.selectedCourseIds.some((id) => Number(id) === Number(courseId))
        },

        isMandatoryWorkspaceChoice(choice) {
            return choice.family_ids.map(Number).indexOf(Number(this.config.corso_id)) !== -1
        },

        isWorkspaceChoiceSelected(choice) {
            return choice.family_ids.every((id) => this.isWorkspaceCourseSelected(id))
        },

        canAddWorkspaceChoice(choice) {
            const selected = new Set(this.selectedCourseIds.map(Number))
            choice.family_ids.forEach((id) => selected.add(Number(id)))
            return selected.size <= this.workspaceCourseLimit
        },

        workspaceChoiceColor(choice) {
            const loaded = (this.workspace.courses || []).find((course) => choice.family_ids.indexOf(Number(course.id)) !== -1)
            return loaded && loaded.color ? {backgroundColor: loaded.color} : {}
        },

        async toggleWorkspaceCourse(course, checked) {
            const familyIds = (course.family_ids && course.family_ids.length ? course.family_ids : [course.id]).map((id) => Number(id))
            const previous = this.selectedCourseIds.slice()
            let next = previous.slice()

            if (checked) {
                next = next.concat(familyIds)
            } else {
                next = next.filter((id) => familyIds.indexOf(Number(id)) === -1)
            }

            next.push(Number(this.config.corso_id))
            const base = this.workspaceCatalog.find((item) => Number(item.id) === Number(this.config.corso_id))
            if (base && base.family_ids) next = next.concat(base.family_ids.map((id) => Number(id)))
            next = Array.from(new Set(next))

            if (next.length > this.workspaceCourseLimit) {
                this.workspaceCourseMessage = 'Puoi visualizzare al massimo ' + this.workspaceCourseLimit + ' calendari contemporaneamente. Rimuovine uno prima di aggiungerne altri.'
                return
            }

            this.workspaceCourseLoading = true
            this.workspaceCourseMessage = ''
            this.selectedCourseIds = next
            this.selection = Object.assign({}, this.selection, { modulo_id: null, docente_id: null })
            try {
                const loaded = await this.reloadCalendar()
                if (!loaded) {
                    this.selectedCourseIds = previous
                    this.workspaceCourseMessage = 'Non è stato possibile aggiornare i calendari selezionati. Riprova oppure riduci il numero di corsi.'
                }
            } finally {
                this.workspaceCourseLoading = false
            }
        },

        findById(items, id) {
            if (!Array.isArray(items) || !id) return null

            return items.find((item) => {
                return Number(item.id) === Number(id)
            }) || null
        },

        normalizeNullableInt(value) {
            if (value === null || typeof value === 'undefined' || value === '') {
                return null
            }

            const parsed = parseInt(value, 10)
            return Number.isNaN(parsed) || parsed <= 0 ? null : parsed
        },

        formatHours(value) {
            const rounded = Math.round(Number(value || 0) * 100) / 100
            return Number.isInteger(rounded) ? rounded : rounded.toFixed(2)
        },

        formatDate(value) {
            if (!value) return '--/--/----'

            const parts = String(value).slice(0, 10).split('-')
            if (parts.length !== 3) return value

            return parts[2] + '/' + parts[1] + '/' + parts[0]
        },

        addDays(date, days) {
            const d = new Date(String(date) + 'T00:00:00')
            d.setDate(d.getDate() + days)

            return d.toISOString().slice(0, 10)
        },

        isoWeekday(date) {
            const d = new Date(String(date) + 'T00:00:00')
            const day = d.getDay()
            return day === 0 ? 7 : day
        },

        getCellDuration(cell) {
            const start = cell.start || cell.hour || (cell.slot ? cell.slot.start : null)
            const end = cell.end || (cell.slot ? cell.slot.end : null) || this.addOneHour(start)

            if (!start || !end) return 1

            const startParts = String(start).slice(0, 5).split(':').map(Number)
            const endParts = String(end).slice(0, 5).split(':').map(Number)
            const startMinutes = (startParts[0] || 0) * 60 + (startParts[1] || 0)
            const endMinutes = (endParts[0] || 0) * 60 + (endParts[1] || 0)
            const diff = (endMinutes - startMinutes) / 60

            return diff > 0 ? diff : 1
        },

        getRecurringDefaultOccurrences(remainingHours, slotDuration) {
            const remaining = Number(remainingHours || 0)
            const duration = Number(slotDuration || 1)

            if (remaining <= 0 || duration <= 0) return 1

            return Math.max(1, Math.min(Math.floor(remaining / duration), 100))
        },

        extractStatsRemaining(stats) {
            if (!stats) return 0

            const modulo = stats.modulo || {}
            const docente = stats.docente || {}
            const moduloResidue = typeof modulo.residue !== 'undefined'
                ? Number(modulo.residue || 0)
                : Number(stats.modulo_residue || 0)
            const docenteResidue = typeof docente.residue !== 'undefined'
                ? Number(docente.residue || 0)
                : Number(stats.docente_residue || 0)

            if (moduloResidue > 0 && docenteResidue > 0) {
                return Math.min(moduloResidue, docenteResidue)
            }

            return Math.max(moduloResidue, docenteResidue, 0)
        },

        getResidueClass(value) {
            const v = Number(value || 0)

            if (v === 0) return 'context-kpi-danger'
            if (v <= 3) return 'context-kpi-warning'
            return 'context-kpi-success'
        },

        async onFiltersUpdate(payload) {
            payload = payload || {}

            const nextRange = payload.range || this.range
            const nextWeekdays = payload.weekdays || this.weekdays
            const nextMode = payload.mode || this.mode
            const nextAule = payload.aule || this.selectedAule || []

            const rangeChanged =
                !this.range ||
                nextRange.from !== this.range.from ||
                nextRange.to !== this.range.to

            const weekdaysChanged =
                JSON.stringify(nextWeekdays || []) !== JSON.stringify(this.weekdays || [])

            const auleChanged =
                JSON.stringify(nextAule || []) !== JSON.stringify(this.selectedAule || [])

            const mustReload = rangeChanged || weekdaysChanged || auleChanged

            console.log('[CalendarV2] filters update', payload)
            console.log('[CalendarV2] mustReload', mustReload)

            this.range = nextRange
            this.weekdays = nextWeekdays
            this.mode = nextMode
            this.selectedAule = nextAule

            if (mustReload) {
                if (rangeChanged) this.syncRangeToQuery(nextRange)
                await this.reloadCalendar()
            }
        },

        buildRangePayload() {
            return {
                corso_id: this.config.corso_id,
                course_ids: this.selectedCourseIds,
                from: this.range.from,
                to: this.range.to,
                weekdays: this.weekdays,
                aule: this.normalizeAulePayload(this.selectedAule)
            }
        },
        calculateHours(start, end) {
            if (!start || !end) return 1

            const s = String(start).split(':')
            const e = String(end).split(':')

            const startMinutes = (parseInt(s[0] || '0', 10) * 60) + parseInt(s[1] || '0', 10)
            const endMinutes = (parseInt(e[0] || '0', 10) * 60) + parseInt(e[1] || '0', 10)

            return Math.max(0, (endMinutes - startMinutes) / 60)
        },

        async reloadCalendar() {
            console.log('[CalendarV2] reloadCalendar start')
            this.loading = true
            this.error = null
            this.diagnostics = null

            try {
                const data = await api.loadCalendar(this.config, this.buildRangePayload())

                const meta = data.meta || {}

                this.corso = meta.corso || {}
                this.days = data.days || []
                this.hours = data.hours || []
                this.calendarSlots = data.slots || []
                this.availability = data.availability || {}
                this.availabilityByCourse = data.availability_by_course || {}
                this.workspace = data.workspace || {mode: 'single', groups: [], courses: [meta.corso || {}]}
                this.selectedCourseIds = (this.workspace.course_ids || this.selectedCourseIds).map((id) => Number(id))
                this.meta = {
                    moduli: meta.moduli || [],
                    docenti: meta.docenti || [],
                    tutors: meta.tutors || [],
                    aule: meta.aule || []
                }

                if (data.range) {
                    this.range = {
                        from: data.range.from,
                        to: data.range.to
                    }
                    this.weekdays = data.range.weekdays || this.weekdays
                    this.selectedAule = data.range.aule || this.selectedAule
                }
                return true
            } catch (error) {
                console.error('Errore caricamento calendario v2', error)
                this.error = error
                this.handleApiError(error, 'Impossibile caricare il calendario')
                return false
            } finally {
                console.log('[CalendarV2] reloadCalendar end')
                this.loading = false
            }
        },

        async updateSelectionStats() {
            if (!this.selection.modulo_id || !this.selection.docente_id) {
                this.selectionStats = {}
                return
            }

            try {
                this.selectionStats = await api.stats(this.config, {
                    corso_id: this.activeCourseId,
                    modulo_id: this.selection.modulo_id,
                    docente_id: this.selection.docente_id
                })
                this.syncSelectedModuloMeta()
            } catch (error) {
                console.error('Errore caricamento statistiche modulo/docente', error)
                this.selectionStats = {}
            }
        },

        syncSelectedModuloMeta() {
            const moduloId = this.selection && this.selection.modulo_id
                ? Number(this.selection.modulo_id)
                : null

            if (!moduloId || !this.selectionStats || !this.selectionStats.modulo || !Array.isArray(this.meta.moduli)) {
                return
            }

            const stats = this.selectionStats.modulo
            const total = typeof stats.totale !== 'undefined'
                ? Number(stats.totale || 0)
                : Number(stats.total_hours || 0)
            const planned = typeof stats.pianificate !== 'undefined'
                ? Number(stats.pianificate || 0)
                : Number(stats.planned_hours || 0)
            const remaining = typeof stats.residue !== 'undefined'
                ? Number(stats.residue || 0)
                : Math.max(0, total - planned)

            this.meta = Object.assign({}, this.meta, {
                moduli: this.meta.moduli.map((modulo) => {
                    if (Number(modulo.id) !== moduloId) {
                        return modulo
                    }

                    return Object.assign({}, modulo, {
                        durata: total || Number(modulo.durata || 0),
                        ore_pianificate: planned,
                        ore_residue: Math.max(0, remaining)
                    })
                })
            })
        },

        async runDiagnostics() {
            this.showStats = true
            this.diagnosticsLoading = true

            try {
                this.diagnostics = await api.diagnostics(this.config, this.buildRangePayload())

                if (window.toastr) {
                    const summary = this.diagnostics && this.diagnostics.summary ? this.diagnostics.summary : {}
                    const total = Number(summary.exact_duplicates || 0) +
                        Number(summary.cell_collisions || 0) +
                        Number(summary.tutor_overlaps || 0)

                    if (total > 0) {
                        window.toastr.warning('Diagnostica completata: trovate anomalie calendario.')
                    } else {
                        window.toastr.success('Diagnostica completata: nessuna anomalia rilevata.')
                    }
                }
            } catch (error) {
                console.error('Errore diagnostica calendario v2', error)
                this.diagnostics = null

                if (window.toastr) {
                    window.toastr.error('Impossibile eseguire la diagnostica calendario')
                }
            } finally {
                this.diagnosticsLoading = false
            }
        },

        openCourseHoursModal() {
            const course = this.activeCourse || this.corso
            const visibleHours = this.displayHours
            const currentBand = this.workBand && Number(this.workBand.course_id) === Number(this.activeCourseId)
                ? this.workBand
                : null
            this.courseHoursModal = {
                visible: true,
                loading: false,
                form: {
                    date_from: currentBand ? currentBand.date_from : this.range.from,
                    date_to: currentBand ? currentBand.date_to : this.range.to,
                    ora_inizio: currentBand
                        ? currentBand.ora_inizio
                        : (visibleHours.length ? visibleHours[0] : (course.ora_inizio || '08:00')),
                    ora_fine: currentBand
                        ? currentBand.ora_fine
                        : (visibleHours.length ? this.addOneHour(visibleHours[visibleHours.length - 1]) : (course.ora_fine || '14:00'))
                }
            }
        },

        closeCourseHoursModal() {
            if (this.courseHoursModal.loading) return

            this.courseHoursModal = {
                visible: false,
                loading: false,
                form: {
                    date_from: '',
                    date_to: '',
                    ora_inizio: '',
                    ora_fine: ''
                }
            }
        },

        async applyWorkBand() {
            const form = this.courseHoursModal.form
            if (!form.date_from || !form.date_to || !form.ora_inizio || !form.ora_fine) {
                this.handleApiError(null, 'Inserisci il periodo e la fascia oraria di lavoro')
                return
            }

            if (form.date_from > form.date_to) {
                this.handleApiError(null, 'La data iniziale non può essere successiva alla data finale')
                return
            }

            if (this.timeToMinutes(form.ora_inizio) >= this.timeToMinutes(form.ora_fine)) {
                this.handleApiError(null, 'L’ora di uscita deve essere successiva all’ora di ingresso')
                return
            }

            const startMinute = String(form.ora_inizio).slice(3, 5)
            const endMinute = String(form.ora_fine).slice(3, 5)
            if (['00', '30'].indexOf(startMinute) === -1 || endMinute !== startMinute) {
                this.handleApiError(null, 'La fascia deve usare una fase :00 oppure :30 e mantenere lezioni complete da 60 minuti')
                return
            }
            if (startMinute !== this.activeMinutePhase) {
                this.handleApiError(null, 'Il corso attivo usa la fase :' + this.activeMinutePhase + '. Mantieni la stessa fase nella nuova fascia di lavoro')
                return
            }

            this.courseHoursModal.loading = true
            const previousRange = Object.assign({}, this.range)
            const previousBand = this.workBand ? Object.assign({}, this.workBand) : null
            const nextRange = {from: form.date_from, to: form.date_to}
            const rangeChanged = nextRange.from !== this.range.from || nextRange.to !== this.range.to
            this.workBand = {
                course_id: this.activeCourseId,
                date_from: form.date_from,
                date_to: form.date_to,
                ora_inizio: form.ora_inizio,
                ora_fine: form.ora_fine
            }
            this.range = nextRange
            this.syncRangeToQuery(nextRange)

            if (rangeChanged) {
                const loaded = await this.reloadCalendar()
                if (!loaded) {
                    this.range = previousRange
                    this.workBand = previousBand
                    this.syncRangeToQuery(previousRange)
                    this.courseHoursModal.loading = false
                    return
                }
            }

            this.courseHoursModal.loading = false
            this.closeCourseHoursModal()
            if (window.toastr) window.toastr.info('Fascia di lavoro applicata soltanto alla vista corrente')
        },

        resetWorkBand() {
            this.workBand = null
            if (window.toastr) window.toastr.info('Vista ripristinata sull’orario del corso e sulle lezioni salvate')
        },

        timeToMinutes(time) {
            const parts = String(time || '').slice(0, 5).split(':').map(Number)
            return (parts[0] || 0) * 60 + (parts[1] || 0)
        },

        generateHours(start, end) {
            const hours = []
            let current = this.timeToMinutes(start)
            const last = this.timeToMinutes(end)

            while (current < last) {
                const hour = String(Math.floor(current / 60)).padStart(2, '0')
                const minute = String(current % 60).padStart(2, '0')
                hours.push(hour + ':' + minute)
                current += 60
            }

            return hours
        },

        syncRangeToQuery(range) {
            if (!window.history || !window.URL || !range || !range.from || !range.to) return

            const url = new URL(window.location.href)
            url.searchParams.set('from', range.from)
            url.searchParams.set('to', range.to)
            window.history.replaceState({}, '', url.toString())
        },

        openDayActions(day) {
            if (this.mode !== 'modifica') return

            this.dayActionModal = {
                visible: true,
                day: day
            }
        },

        closeDayActions() {
            this.dayActionModal = {
                visible: false,
                day: null
            }
        },

        copyDay(operation) {
            const day = this.dayActionModal.day || {}
            if (!day.date) return

            const count = this.dayActionSlots.length
            if (!count) {
                if (window.toastr) {
                    window.toastr.warning('La giornata selezionata non contiene pianificazioni.')
                }
                return
            }

            this.dayClipboard = {
                operation: operation === 'cut' ? 'cut' : 'copy',
                corso_id: this.activeCourseId,
                from: day.date,
                label: day.label || day.date,
                count: count
            }

            if (window.toastr) {
                window.toastr.success(operation === 'cut' ? 'Giornata tagliata in clipboard' : 'Giornata copiata in clipboard')
            }
        },

        async previewPasteDay() {
            const targetDay = this.dayActionModal.day || {}
            if (!this.dayClipboard || !targetDay.date) return

            this.pasteModal = {
                visible: true,
                loading: true,
                applying: false,
                plan: null,
                targetDay: targetDay
            }

            try {
                const plan = await api.dayPastePreview(this.config, {
                    corso_id: this.dayClipboard.corso_id,
                    operation: this.dayClipboard.operation,
                    from: this.dayClipboard.from,
                    to: targetDay.date
                })

                this.pasteModal.loading = false
                this.pasteModal.plan = plan
            } catch (error) {
                this.pasteModal.loading = false
                this.handleApiError(error, 'Impossibile preparare l’anteprima della giornata')
            }
        },

        cancelPasteDay() {
            if (this.pasteModal.applying) return

            this.pasteModal = {
                visible: false,
                loading: false,
                applying: false,
                plan: null,
                targetDay: null
            }
        },

        async applyPasteDay() {
            if (!this.pasteModal.plan || !this.pasteModal.plan.valid || this.pasteModal.applying) return

            this.pasteModal.applying = true

            try {
                const result = await api.dayPasteApply(this.config, {
                    corso_id: this.dayClipboard.corso_id,
                    operation: this.pasteModal.plan.operation,
                    from: this.pasteModal.plan.from,
                    to: this.pasteModal.plan.to
                })

                if (result && result.to && Array.isArray(result.target_slots)) {
                    this.replaceDaySlots(result.to, result.target_slots)
                }

                if (result && result.operation === 'cut' && result.from && Array.isArray(result.source_slots)) {
                    this.replaceDaySlots(result.from, result.source_slots)
                    this.dayClipboard = null
                }

                if (window.toastr) {
                    window.toastr.success('Giornata incollata correttamente')
                }

                this.pasteModal = {
                    visible: false,
                    loading: false,
                    applying: false,
                    plan: null,
                    targetDay: null
                }
                this.closeDayActions()
                await this.refreshAvailability()
                await this.updateSelectionStats()
            } catch (error) {
                this.pasteModal.applying = false
                this.handleApiError(error, 'Impossibile incollare la giornata')
                await this.reloadCalendar()
            }
        },

        openCellStaffModal(cell) {
            if (this.mode !== 'modifica') return

            const slot = cell && cell.slot ? cell.slot : {}
            const codocenteId = this.normalizeNullableInt(slot.codocente_id)
            const tutorId = this.normalizeNullableInt(slot.tutor_id)

            this.staffModal = {
                visible: true,
                loading: false,
                cell: cell,
                form: {
                    tutor_id: tutorId,
                    codocente_id: codocenteId
                },
                codocenteSearch: slot.codocente_nome || '',
                codocenteLabel: slot.codocente_nome || '',
                codocenteOptions: [],
                codocenteLoading: false,
                codocenteTimer: null,
                showCodocenteResults: false
            }
        },

        closeStaffModal() {
            if (this.staffModal.loading) return

            if (this.staffModal.codocenteTimer) {
                clearTimeout(this.staffModal.codocenteTimer)
            }

            this.staffModal = {
                visible: false,
                loading: false,
                cell: null,
                form: {
                    tutor_id: null,
                    codocente_id: null
                },
                codocenteSearch: '',
                codocenteLabel: '',
                codocenteOptions: [],
                codocenteLoading: false,
                codocenteTimer: null,
                showCodocenteResults: false
            }
        },

        onStaffCodocenteSearch() {
            const term = String(this.staffModal.codocenteSearch || '').trim()

            this.staffModal.codocenteLabel = ''
            this.staffModal.form.codocente_id = null

            if (this.staffModal.codocenteTimer) {
                clearTimeout(this.staffModal.codocenteTimer)
            }

            if (term.length < 2) {
                this.staffModal.codocenteOptions = []
                return
            }

            this.staffModal.codocenteTimer = setTimeout(() => {
                this.searchStaffCodocente(term)
            }, 250)
        },

        searchStaffCodocente(term) {
            if (!this.config || !this.config.api || !this.config.api.teacherSuggest) {
                return
            }

            this.staffModal.codocenteLoading = true

            api.teacherSuggest(this.config, {
                q: term,
                search: term,
                term: term
            })
                .then((response) => {
                    this.staffModal.codocenteOptions = response && response.results ? response.results : []
                    this.staffModal.showCodocenteResults = true
                })
                .catch((error) => {
                    console.error('Errore autocomplete co-docente cella', error)
                    this.staffModal.codocenteOptions = []
                })
                .finally(() => {
                    this.staffModal.codocenteLoading = false
                })
        },

        selectStaffCodocente(item) {
            this.staffModal.form.codocente_id = this.normalizeNullableInt(item.id)
            this.staffModal.codocenteLabel = item.full_name || item.text || item.nome || ''
            this.staffModal.codocenteSearch = this.staffModal.codocenteLabel
            this.staffModal.codocenteOptions = []
            this.staffModal.showCodocenteResults = false
        },

        clearStaffCodocente() {
            this.staffModal.form.codocente_id = null
            this.staffModal.codocenteLabel = ''
            this.staffModal.codocenteSearch = ''
            this.staffModal.codocenteOptions = []
            this.staffModal.showCodocenteResults = false
        },

        async saveStaffModal() {
            const cell = this.staffModal.cell || {}
            const slot = cell.slot || {}
            const giornataId = slot.giornata_id || slot.id

            if (!giornataId) {
                this.handleApiError(null, 'Impossibile individuare la pianificazione da aggiornare')
                return
            }

            this.staffModal.loading = true

            try {
                const response = await api.updateStaff(this.config, {
                    corso_id: Number(slot.corso_id || this.activeCourseId),
                    giornata_id: giornataId,
                    date: cell.date || slot.date,
                    start: cell.start || cell.hour || slot.start,
                    end: cell.end || slot.end || this.addOneHour(cell.hour || slot.start),
                    tutor_id: this.normalizeNullableInt(this.staffModal.form.tutor_id),
                    codocente_id: this.normalizeNullableInt(this.staffModal.form.codocente_id)
                })

                if (response && response.date && Array.isArray(response.slots)) {
                    this.replaceDaySlots(response.date, response.slots)
                } else {
                    await this.reloadCalendar()
                }

                if (window.toastr) {
                    window.toastr.success('Cella aggiornata')
                }

                this.staffModal.loading = false
                this.closeStaffModal()
                await this.refreshAvailability()
                await this.updateSelectionStats()
            } catch (error) {
                this.staffModal.loading = false
                this.handleApiError(error, 'Impossibile aggiornare la cella')
                await this.reloadCalendar()
            }
        },

        async openRecurringModalFromStaff() {
            const cell = this.staffModal.cell
            if (!cell || !cell.slot) return

            const sourceDate = cell.date || cell.slot.date
            const weekday = this.isoWeekday(sourceDate)
            const sourceCell = Object.assign({}, cell)
            const slot = cell.slot || {}
            const slotDuration = this.getCellDuration(cell)
            let remainingHours = 0

            const selectedModulo = Number(this.selection.modulo_id || 0)
            const selectedDocente = Number(this.selection.docente_id || 0)
            const slotModulo = Number(slot.modulo_id || 0)
            const slotDocente = Number(slot.docente_id || 0)

            if (
                selectedModulo &&
                selectedDocente &&
                selectedModulo === slotModulo &&
                selectedDocente === slotDocente
            ) {
                remainingHours = this.extractStatsRemaining(this.selectionStats)
            }

            if (!remainingHours && slotModulo && slotDocente) {
                try {
                    const stats = await api.stats(this.config, {
                        corso_id: Number(slot.corso_id || this.activeCourseId),
                        modulo_id: slotModulo,
                        docente_id: slotDocente
                    })
                    remainingHours = this.extractStatsRemaining(stats)
                } catch (error) {
                    console.error('Errore caricamento residue ricorrenza', error)
                }
            }

            if (this.staffModal.codocenteTimer) {
                clearTimeout(this.staffModal.codocenteTimer)
            }

            this.recurringModal = {
                visible: true,
                loading: false,
                applying: false,
                cell: sourceCell,
                plan: null,
                remaining_hours: remainingHours,
                slot_duration: slotDuration,
                form: {
                    from: this.addDays(sourceDate, 1),
                    to: this.range && this.range.to ? this.range.to : this.addDays(sourceDate, 60),
                    weekdays: [weekday],
                    stop_mode: 'module_remaining',
                    max_occurrences: this.getRecurringDefaultOccurrences(remainingHours, slotDuration),
                    skip_conflicts: true
                }
            }

            this.staffModal = {
                visible: false,
                loading: false,
                cell: null,
                form: {
                    tutor_id: null,
                    codocente_id: null
                },
                codocenteSearch: '',
                codocenteLabel: '',
                codocenteOptions: [],
                codocenteLoading: false,
                codocenteTimer: null,
                showCodocenteResults: false
            }
        },

        closeRecurringModal() {
            if (this.recurringModal.applying) return

            this.recurringModal = {
                visible: false,
                loading: false,
                applying: false,
                cell: null,
                plan: null,
                remaining_hours: 0,
                slot_duration: 1,
                form: {
                    from: '',
                    to: '',
                    weekdays: [],
                    stop_mode: 'module_remaining',
                    max_occurrences: 50,
                    skip_conflicts: true
                }
            }
        },

        buildRecurringPayload() {
            const cell = this.recurringModal.cell || {}
            const slot = cell.slot || {}

            return {
                corso_id: Number(slot.corso_id || this.activeCourseId),
                giornata_id: slot.giornata_id || slot.id,
                date: cell.date || slot.date,
                start: cell.start || cell.hour || slot.start,
                end: cell.end || slot.end || this.addOneHour(cell.hour || slot.start),
                from: this.recurringModal.form.from,
                to: this.recurringModal.form.to,
                weekdays: this.recurringModal.form.weekdays,
                stop_mode: this.recurringModal.form.stop_mode,
                max_occurrences: this.recurringModal.form.max_occurrences,
                skip_conflicts: this.recurringModal.form.skip_conflicts
            }
        },

        async previewRecurring() {
            if (!this.recurringModal.form.weekdays.length) {
                this.handleApiError(null, 'Seleziona almeno un giorno della settimana')
                return
            }

            this.recurringModal.loading = true
            this.recurringModal.plan = null

            try {
                this.recurringModal.plan = await api.recurringPreview(this.config, this.buildRecurringPayload())
            } catch (error) {
                this.handleApiError(error, 'Impossibile generare l’anteprima ricorrente')
            } finally {
                this.recurringModal.loading = false
            }
        },

        async applyRecurring() {
            if (!this.recurringModal.plan || !this.recurringModal.plan.valid || this.recurringModal.applying) return

            this.recurringModal.applying = true

            try {
                const result = await api.recurringApply(this.config, this.buildRecurringPayload())

                if (result && result.slots_by_date) {
                    Object.keys(result.slots_by_date).forEach((date) => {
                        this.replaceDaySlots(date, result.slots_by_date[date])
                    })
                } else {
                    await this.reloadCalendar()
                }

                if (window.toastr) {
                    window.toastr.success('Pianificazione ricorrente applicata')
                }

                this.recurringModal.applying = false
                this.closeRecurringModal()
                this.closeStaffModal()
                await this.refreshAvailability()
                await this.updateSelectionStats()
            } catch (error) {
                this.recurringModal.applying = false
                this.handleApiError(error, 'Impossibile applicare la pianificazione ricorrente')
                await this.reloadCalendar()
            }
        },

        teacherScheduleUrl(teacher, from, to) {
            const params = new URLSearchParams({teacher: teacher, from: from, to: to || from})
            return this.config.teacher_schedule_url + '?' + params.toString()
        },

        openTeacherSchedule(cell) {
            window.open(this.teacherScheduleUrl(this.selection.docente_id, cell.date), '_blank', 'noopener')
        },

        showTeacherConflict(cell) {
            this.scheduleConflictMessage = this.getAvailabilityMessage(cell.availability)
            this.scheduleConflictLinks = [{label: 'Consulta impegni e contatta i tutor', url: this.teacherScheduleUrl(this.selection.docente_id, cell.date)}]
            const conflict = (cell.availability || {}).conflict || {}
            if (conflict.course_url) this.scheduleConflictLinks.unshift({label: 'Apri il corso: ' + (conflict.corso_ref || conflict.corso_titolo), url: conflict.course_url})
        },

        async handleCellClick(cell) {
            if (this.mode !== 'modifica') return

            if (cell.occupied || cell.slot) {
                this.requestDeleteSlot(cell)
            } else {
                await this.addSlot(cell)
            }
        },

        getAvailabilityMessage(availability) {
            if (!availability) {
                return 'Docente già impegnato in questo orario.'
            }

            if (availability.message) {
                return availability.message
            }

            if (availability.reason) {
                return availability.reason
            }

            const conflict = availability.conflict || {}

            if (conflict.corso_ref || conflict.corso_titolo || conflict.modulo_nome) {
                const parts = []

                const corso = this.compactLabel(conflict.corso_ref, conflict.corso_titolo)

                if (corso) {
                    parts.push(corso)
                }

                if (conflict.modulo_nome) {
                    parts.push(conflict.modulo_nome)
                }

                if (conflict.start && conflict.end) {
                    parts.push(conflict.start + ' → ' + conflict.end)
                }

                return 'Docente occupato: ' + parts.join(' · ')
            }

            return 'Docente già impegnato in questo orario.'
        },

        compactLabel(ref, title) {
            ref = String(ref || '').trim()
            title = String(title || '').trim()

            if (ref && title) {
                return ref + ' - ' + title
            }

            if (ref) {
                return ref
            }

            if (title) {
                return title
            }

            return ''
        },

        async addSlot(cell) {
            cell = cell || {}

            if (cell.unavailable) {
                this.showTeacherConflict(cell)
                const message = this.getAvailabilityMessage(cell.availability)

                if (window.toastr) {
                    window.toastr.warning(message)
                } else {
                    alert(message)
                }

                return
            }

            const hasPrimaryStaff = !!(this.selection.docente_id || this.selection.tutor_id)
            if (!this.selection.modulo_id || !hasPrimaryStaff) {
                if (window.toastr) {
                    window.toastr.warning('Seleziona il modulo e almeno un docente oppure un tutor.')
                } else {
                    alert('Seleziona il modulo e almeno un docente oppure un tutor.')
                }

                return
            }
            const slotDuration = this.calculateHours(
                cell.start || cell.hour,
                cell.end || this.addOneHour(cell.start || cell.hour)
            )
            const payload = {
                corso_id: this.activeCourseId,
                date: cell.date,
                start: cell.start || cell.hour,
                end: cell.end || this.addOneHour(cell.start || cell.hour),
                modulo_id: this.selection.modulo_id,
                docente_id: this.selection.docente_id || null,
                tutor_id: this.selection.tutor_id,
                codocente_id: this.selection.codocente_id,
                aula_id: cell.aula_id || this.selection.aula_id || 0,
                durata_tutor: this.selection.tutor_id ? slotDuration : 0
            }
            if (!payload.date || !payload.start || !payload.end) {
                this.handleApiError(null, 'Dati dello slot non completi.')
                return
            }

            const declared = cell.availability && cell.availability.declared_availability
            await this.validateAndSaveSlot(payload, declared && declared.warning ? declared : null)
        },

        async validateAndSaveSlot(payload, declaredWarning) {
            let validation

            try {
                validation = await api.validate(this.config, payload)
            } catch (error) {
                this.handleApiError(error, 'Impossibile verificare la pianificazione')
                return
            }

            if (!validation || !validation.valid) {
                const errors = validation && validation.errors ? validation.errors : {}
                const messages = Object.keys(errors).reduce((all, key) => all.concat(errors[key] || []), [])
                const outsideHours = messages.length === 1 && messages[0].indexOf('fuori dall’intervallo orario') !== -1

                if (outsideHours && !payload.outside_course_hours_exception) {
                    this.outsideHoursModal = {
                        visible: true,
                        loading: false,
                        reason: '',
                        payload: Object.assign({}, payload),
                        declaredWarning: declaredWarning
                    }
                    return
                }

                this.scheduleConflictLinks = (validation && validation.teacher_schedule_links) || []
                this.scheduleConflictMessage = messages.join(' · ')
                const message = messages.join('\n') || 'Pianificazione non valida.'
                if (window.toastr) window.toastr.error(message)
                else alert(message)
                return
            }

            const warnings = (validation.warnings || []).map((warning) => warning.message || warning)
            if (declaredWarning) {
                warnings.push(declaredWarning.message || 'La pianificazione non rientra nella disponibilità dichiarata dal docente.')
            }

            if (warnings.length && !window.confirm(warnings.join('\n') + '\n\nVuoi continuare?')) return

            await this.persistSlotPayload(payload)
        },

        closeOutsideHoursModal() {
            if (this.outsideHoursModal.loading) return
            this.outsideHoursModal = {
                visible: false,
                loading: false,
                reason: '',
                payload: null,
                declaredWarning: null
            }
        },

        async confirmOutsideHoursException() {
            if (!this.outsideHoursModal.payload || !this.outsideHoursModal.reason || this.outsideHoursModal.loading) return

            this.outsideHoursModal.loading = true
            const payload = Object.assign({}, this.outsideHoursModal.payload, {
                outside_course_hours_exception: true,
                calendar_exception_reason: this.outsideHoursModal.reason
            })
            const declaredWarning = this.outsideHoursModal.declaredWarning

            try {
                const validation = await api.validate(this.config, payload)
                if (!validation || !validation.valid) {
                    const errors = validation && validation.errors ? validation.errors : {}
                    const messages = Object.keys(errors).reduce((all, key) => all.concat(errors[key] || []), [])
                    throw new Error(messages.join('\n') || 'Eccezione non valida.')
                }

                const warnings = (validation.warnings || []).map((warning) => warning.message || warning)
                if (declaredWarning) warnings.push(declaredWarning.message)
                if (warnings.length && !window.confirm(warnings.join('\n') + '\n\nVuoi continuare?')) {
                    this.outsideHoursModal.loading = false
                    return
                }

                const saved = await this.persistSlotPayload(payload)
                this.outsideHoursModal.loading = false
                if (saved) this.closeOutsideHoursModal()
            } catch (error) {
                this.outsideHoursModal.loading = false
                this.handleApiError(error, 'Impossibile aggiungere l’eccezione')
            }
        },

        async persistSlotPayload(payload) {
            try {
                const response = await api.save(this.config, payload)
                if (response && response.slots && Array.isArray(response.slots)) {
                    this.replaceDaySlots(response.date || payload.date, response.slots)
                } else if (response && response.slot) {
                    this.upsertSlots([response.slot])
                } else {
                    await this.reloadCalendar()
                }
                if (window.toastr) window.toastr.success('Pianificazione salvata')
                await this.refreshAvailability()
                await this.updateSelectionStats()
                return true
            } catch (error) {
                console.error('Errore salvataggio pianificazione', error)
                this.handleApiError(error, 'Impossibile salvare la pianificazione')
                return false
            }
        },

        async requestAvailabilityConfirmation(payload) {
            if (!payload || !payload.giornata_id) return
            if (!window.confirm('Inviare al docente una email per confermare la disponibilità in questo giorno e orario?')) return

            try {
                const response = await api.requestAvailabilityConfirmation(this.config, {
                    corso_id: Number(payload.slot && payload.slot.corso_id ? payload.slot.corso_id : this.activeCourseId),
                    giornata_id: payload.giornata_id,
                    date: payload.date,
                    start: payload.start,
                    end: payload.end
                })
                const message = response && response.message ? response.message : 'Richiesta inviata al docente.'
                if (window.toastr) window.toastr.success(message)
                else alert(message)
            } catch (error) {
                this.handleApiError(error, 'Impossibile inviare la richiesta di conferma')
            }
        },

        requestDeleteSlot(cell) {
            this.deleteModal = {
                visible: true,
                loading: false,
                cell: cell
            }
        },

        cancelDeleteSlot() {
            if (this.deleteModal.loading) return

            this.deleteModal = {
                visible: false,
                loading: false,
                cell: null
            }
        },

        async confirmDeleteSlot() {
            if (!this.deleteModal.cell || this.deleteModal.loading) return

            this.deleteModal.loading = true
            await this.removeSlot(this.deleteModal.cell)
        },

        closeDeleteModal() {
            this.deleteModal = {
                visible: false,
                loading: false,
                cell: null
            }
        },

        async removeSlot(cell) {
            const slot = cell.slot || cell
            const giornataId = slot.giornata_id || slot.id
            const start = this.getDeleteCellStart(cell)
            const end = this.getDeleteCellEnd(cell, start)

            if (!giornataId) {
                if (window.toastr) {
                    window.toastr.error('Impossibile individuare la pianificazione da eliminare')
                }
                this.closeDeleteModal()
                return
            }

            const payload = {
                corso_id: Number(slot.corso_id || this.activeCourseId),
                giornata_id: giornataId,
                date: cell.date || slot.date,
                start: start,
                end: end,
                aula_id: slot.aula_id || cell.aula_id || 0
            }

            const previousSlots = this.calendarSlots.slice()
            this.removeLocalSlot(payload)

            try {
                const response = await api.delete(this.config, payload)
                this.applyDeleteResponse(giornataId, response)
                if (window.toastr) {
                    window.toastr.success('Pianificazione rimossa')
                }
                await this.refreshAvailability()
                await this.updateSelectionStats()
                this.closeDeleteModal()
            } catch (error) {
                this.calendarSlots = previousSlots
                console.error('Errore rimozione pianificazione', error)
                this.handleApiError(error, 'Impossibile rimuovere la pianificazione')
                await this.reloadCalendar()
                this.closeDeleteModal()
            }
        },
        getDeleteCellStart(cell) {
            cell = cell || {}
            const slot = cell.slot || cell

            return cell.start || cell.hour || cell.time || slot.hour || slot.time || slot.start
        },
        getDeleteCellEnd(cell, start) {
            cell = cell || {}

            return cell.end || this.addOneHour(start || this.getDeleteCellStart(cell))
        },
        replaceDaySlots(date, slots, courseId) {
            if (!date || !Array.isArray(slots)) return
            const firstSlot = slots.length ? slots[0] : null
            const targetCourseId = Number(courseId || (firstSlot && firstSlot.corso_id) || this.activeCourseId)

            this.calendarSlots = this.calendarSlots.filter((slot) => {
                return !(String(slot.date || slot.day) === String(date) && Number(slot.corso_id) === targetCourseId)
            })

            this.upsertSlots(slots)
        },
        upsertSlots(slots) {
            if (!Array.isArray(slots)) return

            const next = this.calendarSlots.slice()

            slots.forEach((slot) => {
                const index = next.findIndex((item) => {
                    return this.sameSlotKey(item, slot)
                })

                if (index >= 0) {
                    next.splice(index, 1, slot)
                } else {
                    next.push(slot)
                }
            })

            this.calendarSlots = next
        },

        removeLocalSlot(payload) {
            this.calendarSlots = this.calendarSlots.filter((slot) => {
                const sameDate = (slot.date || slot.day) === payload.date
                const sameHour = (slot.hour || slot.time || slot.start) === payload.start
                const sameId = Number(slot.giornata_id || slot.id) === Number(payload.giornata_id)

                return !(sameDate && sameHour && sameId)
            })
        },

        applyDeleteResponse(originalGiornataId, response) {
            response = response || {}

            this.calendarSlots = this.calendarSlots.filter((slot) => {
                return Number(slot.giornata_id || slot.id) !== Number(originalGiornataId)
            })

            if (response.slots && Array.isArray(response.slots)) {
                const firstSlot = response.slots.length ? response.slots[0] : null
                const date = response.date || (firstSlot ? (firstSlot.date || firstSlot.day) : null)

                if (date) {
                    this.replaceDaySlots(date, response.slots)
                } else {
                    this.upsertSlots(response.slots)
                }
            }
        },

        sameSlotKey(a, b) {
            return String(a.date || a.day) === String(b.date || b.day) &&
                String(a.hour || a.time || a.start) === String(b.hour || b.time || b.start) &&
                Number(a.aula_id || 0) === Number(b.aula_id || 0) &&
                Number(a.giornata_id || a.id || 0) === Number(b.giornata_id || b.id || 0)
        },

        normalizeAulePayload(aule) {
            if (!Array.isArray(aule)) return []

            return aule
                .map((item) => {
                    if (item && typeof item === 'object') {
                        return item.id
                    }
                    return item
                })
                .filter((id) => !!id)
        },

        async refreshAvailability() {
            if (!this.range || !this.range.from || !this.range.to || !this.displayHours.length) {
                return
            }

            const docenti = (this.meta.docenti || [])
                .map((docente) => docente.id)
                .filter((id) => !!id)

            if (!docenti.length) {
                this.availability = {}
                return
            }

            try {
                const refreshed = await api.availabilityBulk(this.config, {
                    corso_id: this.activeCourseId,
                    from: this.range.from,
                    to: this.range.to,
                    weekdays: this.weekdays,
                    aule: this.normalizeAulePayload(this.selectedAule),
                    hours: this.displayHours,
                    docenti: docenti
                })
                this.availability = refreshed
                this.$set(this.availabilityByCourse, this.activeCourseId, refreshed)
            } catch (error) {
                console.error('Errore aggiornamento disponibilità calendario v2', error)
                await this.reloadCalendar()
            }
        },

        addOneHour(time) {
            if (!time) return null

            const parts = String(time).split(':')
            const h = parseInt(parts[0] || '0', 10)
            const m = parseInt(parts[1] || '0', 10)
            const d = new Date()
            d.setHours(h, m, 0, 0)
            d.setHours(d.getHours() + 1)

            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
        },

        handleApiError(error, fallbackMessage) {
            let message = fallbackMessage

            if (error && error.response && error.response.data && error.response.data.errors) {
                const errors = error.response.data.errors
                const firstKey = Object.keys(errors)[0]
                if (firstKey && Array.isArray(errors[firstKey])) {
                    message = errors[firstKey][0]
                }
            } else if (error && error.response && error.response.data && error.response.data.message) {
                message = error.response.data.message
            }

            if (window.toastr) {
                window.toastr.error(message)
            }
        },

        async init() {
            await this.reloadCalendar()
        }
    },

    mounted() {
        window.addEventListener('keydown', this.onCalendarWindowKeydown)
        this.init()
    },

    beforeDestroy() {
        window.removeEventListener('keydown', this.onCalendarWindowKeydown)
    }
}
</script>

<style scoped>
.calendar-editor-v2 {
    transition: background-color .15s ease-in-out, border-color .15s ease-in-out;
}

.calendar-workspace-toolbar {
    padding: 10px;
    margin-bottom: 8px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    background: #fff;
}

.workspace-heading,
.workspace-selected-courses,
.workspace-course-suggestions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.workspace-course-count {
    padding: 2px 7px;
    border-radius: 999px;
    background: #e2e8f0;
    color: #475569;
    font-size: 11px;
    font-weight: 700;
}

.workspace-guide-button {
    margin-left: auto;
}

.workspace-guide-button i {
    margin-right: 3px;
}

.workspace-selected-courses {
    margin-top: 8px;
}

.workspace-mode-badge,
.workspace-course-option,
.workspace-selected-chip {
    padding: 4px 8px;
    border: 1px solid #cbd5e1;
    border-radius: 999px;
    background: #f8fafc;
    font-size: 11px;
}

.workspace-mode-articulated,
.workspace-mode-mixed {
    border-color: #c4b5fd;
    background: #f5f3ff;
    color: #6d28d9;
}

.workspace-mode-parallel {
    border-color: #93c5fd;
    background: #eff6ff;
    color: #1d4ed8;
}

.workspace-course-option {
    margin: 0;
    cursor: pointer;
    font-weight: 500;
}

.workspace-course-option:disabled {
    cursor: not-allowed;
    opacity: .55;
}

.workspace-course-option small {
    color: #7c3aed;
}

.workspace-selected-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-color: #60a5fa;
    background: #dbeafe;
    color: #1e3a5f;
    font-weight: 600;
}

.workspace-selected-chip-articulated {
    border-color: #c4b5fd;
    background: #f5f3ff;
    color: #5b21b6;
}

.workspace-selected-chip small {
    color: inherit;
    font-size: 10px;
    font-weight: 500;
}

.workspace-selected-chip button {
    padding: 0 2px;
    border: 0;
    background: transparent;
    color: inherit;
    font-size: 17px;
    line-height: 1;
}

.workspace-course-color {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #60a5fa;
}

.workspace-chip-label {
    display: inline-block;
    max-width: 440px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workspace-add-course {
    border-radius: 999px;
}

.workspace-course-suggestions {
    margin-top: 8px;
    color: #64748b;
    font-size: 11px;
}

.workspace-course-message {
    margin-top: 7px;
    color: #b45309;
    font-size: 12px;
    font-weight: 600;
}

.workspace-help {
    margin: 7px 0 0;
    color: #64748b;
    font-size: 11px;
}

.calendar-mode-consultazione {
    background: #ffffff;
}

.calendar-mode-modifica {
    background: linear-gradient(180deg, rgba(239, 246, 255, .72) 0%, rgba(255, 255, 255, 1) 260px);
}

.calendar-mode-modifica .calendar-edit-context-bar {
    border-color: #93c5fd;
    background: rgba(239, 246, 255, .97);
}

.calendar-mode-consultazione .calendar-edit-context-bar {
    border-color: #e5e7eb;
    background: rgba(248, 250, 252, .96);
}

.calendar-edit-context-bar {
    position: sticky;
    top: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 6px 10px;
    margin-bottom: 8px;
    border: 1px solid #dbeafe;
    border-radius: 6px;
    background: rgba(248, 250, 252, .96);
    box-shadow: 0 4px 12px rgba(15, 23, 42, .06);
    backdrop-filter: blur(4px);
}

.context-main {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
}

.context-kpis {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.context-pill,
.context-item,
.context-kpi {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 12px;
    line-height: 1.2;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    color: #334155;
}

.context-mode {
    background: #1d4ed8;
    border-color: #1d4ed8;
    color: #ffffff;
    font-weight: 700;
}

.context-item strong,
.context-kpi strong {
    margin-right: 3px;
}

.context-kpi-success {
    background: #ecfdf5;
    border-color: #bbf7d0;
    color: #166534;
}

.context-kpi-warning {
    background: #fffbeb;
    border-color: #fde68a;
    color: #92400e;
}

.context-kpi-danger {
    background: #fef2f2;
    border-color: #fecaca;
    color: #991b1b;
}

.calendar-course-summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 10px;
    margin-bottom: 8px;
    border: 1px solid #dbeafe;
    border-radius: 6px;
    background: #f8fbff;
}

.summary-course-title {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    min-width: min(360px, 100%);
    color: #1f2937;
}

.summary-course-title strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
}

.summary-course-title span {
    flex: 0 0 auto;
    color: #64748b;
    font-size: 12px;
}

.summary-metric {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    min-height: 28px;
    padding: 4px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: #ffffff;
    color: #334155;
}

.summary-neutral {
    border-color: #dbeafe;
    background: #ffffff;
    color: #1e3a8a;
}

.summary-metric span {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.summary-metric strong {
    font-size: 14px;
}

.summary-success {
    border-color: #bbf7d0;
    background: #ecfdf5;
    color: #166534;
}

.summary-warning {
    border-color: #fde68a;
    background: #fffbeb;
    color: #92400e;
}

.summary-progress {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 150px;
    font-size: 12px;
    color: #475569;
}

.summary-progress-track {
    position: relative;
    flex: 1;
    height: 7px;
    border-radius: 999px;
    background: #e5e7eb;
    overflow: hidden;
}

.summary-progress-bar {
    height: 100%;
    border-radius: 999px;
    background: #2563eb;
}

.summary-hours {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    color: #334155;
}

.calendar-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    background: rgba(15, 23, 42, .45);
}

.calendar-modal {
    width: min(460px, 100%);
    border-radius: 6px;
    background: #ffffff;
    box-shadow: 0 18px 40px rgba(15, 23, 42, .22);
    overflow: hidden;
}

.calendar-modal-sm {
    width: min(380px, 100%);
}

.calendar-modal-md {
    width: min(560px, 100%);
}

.calendar-modal-lg {
    width: min(760px, 100%);
}

.calendar-multicourse-guide {
    display: flex;
    flex-direction: column;
    width: min(980px, 100%);
    max-height: calc(100vh - 36px);
    outline: none;
}

.calendar-workspace-course-dialog {
    display: flex;
    flex-direction: column;
    width: min(760px, 100%);
    max-height: calc(100vh - 36px);
    outline: none;
}

.workspace-course-dialog-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
}

.workspace-course-dialog-header p {
    margin: 5px 0 0;
    color: #64748b;
}

.workspace-course-dialog-body {
    overflow-y: auto;
}

.workspace-course-dialog-tools {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: end;
    gap: 5px 14px;
    margin-bottom: 14px;
}

.workspace-course-dialog-tools label {
    grid-column: 1 / -1;
    margin: 0;
}

.workspace-course-dialog-tools span {
    padding-bottom: 8px;
    color: #64748b;
    white-space: nowrap;
}

.workspace-course-dialog-list {
    display: grid;
    gap: 7px;
}

.workspace-course-dialog-option {
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    text-align: left;
    border: 1px solid #dbe3ec;
    border-radius: 6px;
    background: #fff;
}

.workspace-course-dialog-option:hover:not(:disabled) {
    border-color: #60a5fa;
    background: #eff6ff;
}

.workspace-course-dialog-option.is-selected {
    border-color: #60a5fa;
    background: #dbeafe;
}

.workspace-course-dialog-option.is-articulated {
    border-left: 4px solid #8b5cf6;
}

.workspace-course-dialog-option > i {
    color: #2563eb;
    font-size: 18px;
}

.workspace-course-dialog-option strong,
.workspace-course-dialog-option small {
    display: block;
}

.workspace-course-dialog-option small {
    margin-top: 3px;
    color: #64748b;
    font-weight: 400;
}

.workspace-course-dialog-option em {
    color: #64748b;
    font-size: 11px;
    font-style: normal;
    white-space: nowrap;
}

.workspace-course-dialog-option:disabled {
    cursor: not-allowed;
    opacity: .58;
}

.workspace-course-dialog-empty {
    padding: 28px;
    text-align: center;
    color: #64748b;
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
}

.multicourse-guide-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    padding: 18px 20px 14px;
}

.multicourse-guide-header h4 {
    margin-top: 3px;
    font-size: 22px;
}

.multicourse-guide-header p {
    max-width: 760px;
    margin: 6px 0 0;
    color: #64748b;
    line-height: 1.45;
}

.multicourse-guide-kicker {
    color: #2563eb;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .08em;
    text-transform: uppercase;
}

.multicourse-guide-close {
    flex: 0 0 auto;
    padding: 0 3px;
    border: 0;
    background: transparent;
    color: #64748b;
    font-size: 30px;
    line-height: 1;
}

.multicourse-guide-body {
    overflow-y: auto;
    padding: 18px 20px 22px;
}

.multicourse-guide-modes {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.multicourse-guide-modes article {
    padding: 14px;
    border: 1px solid #dbe3ec;
    border-radius: 6px;
    background: #f8fafc;
}

.multicourse-guide-modes h5,
.multicourse-guide-steps h5 {
    margin: 9px 0 5px;
    color: #1f2937;
    font-size: 15px;
    font-weight: 700;
}

.multicourse-guide-modes p,
.multicourse-guide-notes p {
    margin: 0;
    line-height: 1.5;
}

.multicourse-guide-steps {
    margin-top: 15px;
    padding: 15px 17px;
    border-left: 4px solid #60a5fa;
    background: #eff6ff;
}

.calendar-guide-first-steps {
    margin-top: 0;
}

.calendar-guide-availability-note {
    margin-top: 16px;
}

.calendar-guide-section-title {
    margin: 22px 0 10px;
    color: #244966;
    font-weight: 700;
}

.multicourse-guide-steps h5 {
    margin-top: 0;
}

.multicourse-guide-steps ol {
    margin: 0;
    padding-left: 21px;
}

.multicourse-guide-steps li {
    margin-bottom: 6px;
    line-height: 1.5;
}

.multicourse-guide-notes {
    display: grid;
    gap: 8px;
    margin-top: 15px;
    padding: 13px 15px;
    border-radius: 5px;
    background: #f8fafc;
}

.calendar-modal-header {
    padding: 14px 16px 8px;
    border-bottom: 1px solid #e5e7eb;
}

.calendar-modal-header h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #111827;
}

.calendar-modal-body {
    padding: 14px 16px;
    color: #334155;
}

.calendar-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px 14px;
    background: #f8fafc;
    border-top: 1px solid #e5e7eb;
}

@media (max-width: 767px) {
    .workspace-guide-button {
        width: 100%;
        margin-left: 0;
    }

    .calendar-modal-backdrop {
        padding: 8px;
    }

    .calendar-multicourse-guide {
        max-height: calc(100vh - 16px);
    }

    .calendar-workspace-course-dialog {
        max-height: calc(100vh - 16px);
    }

    .workspace-chip-label {
        max-width: 240px;
    }

    .workspace-course-dialog-tools {
        grid-template-columns: 1fr;
    }

    .workspace-course-dialog-tools label {
        grid-column: auto;
    }

    .workspace-course-dialog-option {
        grid-template-columns: 22px minmax(0, 1fr);
    }

    .workspace-course-dialog-option em {
        grid-column: 2;
    }

    .multicourse-guide-modes {
        grid-template-columns: 1fr;
    }
}

.day-action-list {
    display: grid;
    gap: 8px;
}

.calendar-clipboard-info {
    padding: 8px 10px;
    border: 1px solid #bfdbfe;
    border-radius: 4px;
    background: #eff6ff;
    color: #1e3a8a;
}

.calendar-modal-loading {
    padding: 18px;
    text-align: center;
    color: #475569;
}

.calendar-preview-alert {
    padding: 8px 10px;
    border-radius: 4px;
    margin-bottom: 8px;
    font-size: 13px;
}

.calendar-preview-warning {
    border: 1px solid #fde68a;
    background: #fffbeb;
    color: #92400e;
}

.calendar-preview-error {
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #991b1b;
}

.calendar-preview-table {
    display: grid;
    gap: 8px;
    max-height: 420px;
    overflow: auto;
}

.calendar-preview-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: start;
    padding: 10px;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background: #ffffff;
}

.calendar-preview-row-error {
    border-color: #fecaca;
    background: #fff7f7;
}

.calendar-preview-main {
    display: grid;
    gap: 2px;
    min-width: 0;
}

.calendar-preview-main span,
.calendar-preview-main small {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.calendar-preview-errors {
    max-width: 260px;
    color: #991b1b;
    font-size: 12px;
    line-height: 1.3;
}

.calendar-preview-ok {
    padding: 2px 8px;
    border-radius: 999px;
    background: #dcfce7;
    color: #166534;
    font-size: 12px;
    font-weight: 700;
}

.calendar-slot-summary {
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background: #f8fafc;
}

.codocente-autocomplete {
    position: relative;
}

.codocente-results {
    position: absolute;
    z-index: 10020;
    left: 0;
    right: 0;
    max-height: 220px;
    overflow: auto;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    background: #ffffff;
    box-shadow: 0 8px 18px rgba(15, 23, 42, .14);
}

.codocente-result-item {
    padding: 7px 9px;
    cursor: pointer;
    font-size: 13px;
}

.codocente-result-item:hover {
    background: #eff6ff;
}

.selected-codocente {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 3px 8px;
    border-radius: 999px;
    background: #eef2ff;
    color: #3730a3;
    font-size: 12px;
}

.cell-advanced-actions {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
}

.recurring-form-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
}

.recurring-weekdays {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.recurring-weekdays label,
.recurring-checkbox {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
}

.recurring-preview-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 12px 0;
}

.recurring-preview-table {
    margin-top: 8px;
}

@media (max-width: 991px) {
    .calendar-edit-context-bar {
        align-items: flex-start;
        flex-direction: column;
    }

    .context-kpis {
        flex-wrap: wrap;
    }

    .recurring-form-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
